// store/index.js

import Vue from "vue";
import Vuex from "vuex";
import { cloneDeep, forEach, omit } from "lodash";
import { sortByName, getToken } from "@/utils";
import { sortByKeyword } from "@/utils";
import {
  agencyPlans,
  unlimitedServices,
  watchdogOnlyServices,
  agencyPlansWithAdditionalSeatsAvailable,
  correspondingSeatPlans,
  popAuthorityPlans,
  whiteGlovePlans,
} from "@/components/plans_constants";
import { getPureServicePkgName } from "@/utils";
import signup from "@/store/modules/signup";

// imports of AJAX functions will go here
import {
  signupStart,
  signupVerify,
  signupResend,
  fetchPlanCatalog,
  previewCoupon,
  loginWithPassword,
  loginConfirmCode,
  loginResendCode,
  requestPasswordReset,
  validateResetToken,
  resetPasswordWithToken,
  socialExchange,
  socialConfirm,
  socialLink,
  createCheckoutSession,
  getCheckoutStatus,
  fetchFairUsePolicy,
  saveWelcomeAnswers,
  setPendingFirstRun,
  clearPendingFirstRun,
  fetchUser,
  updateUser,
  fetchSite,
  fetchSites,
  updateSite,
  updateSitePopImpactScore,
  getSiteContentAudits,
  createSiteContentAudit,
  updateSiteContentAudit,
  createBalanceTopUpOrder,
  updateBalanceTopUpOrder,
  fetchBulkRun,
  askForHelpWithKeywords,
  getSilos,
  getSilo,
  createSilo,
  getSiloKeywords,
  getSiloKeyword,
  getSiloPDFReport,
  getSiloWordReport,
  addKeywordToSilo,
  removeKeywordFromSilo,
  restoreSiloKeyword,
  bulkRemoveKeywordFromSilo,
  bulkRestoreSiloKeyword,
  updateSiloKeyword,
  deleteSilo,
  bulkDeleteSilos,
  updateSilo,
  moveSiloKeywords,
  getAvailableSiloContentReportPackages,
  getAvailableSiloContentReportDeliveryOptionsForSite,
  getSiloContentReportOrders,
  updateSiloContentReportOrder,
  getCartItems,
  getCartInvoices,
  closeCartInvoice,
  setCartItemsAsPaid,
  fetchBulkRuns,
  getWhiteGloveOrders,
  getWhiteGloveMissionControlSiteStats,
  deleteBulkRun,
  downloadBulkRunZip,
  fetchPages,
  fetchPageRuns,
  fetchPageRunsBySearchReportId,
  fetchPageRunsForExtension,
  fetchPageRunForExtension,
  fetchPageRun,
  fetchPageRunBySecretKeyOrPageRunId,
  fetchLsaPhrases,
  createSecretKey,
  fetchData,
  fetchVariations,
  createStripeCardAdditionLink,
  fetchPaymentSources,
  fetchProxies,
  updatePageRunNote,
  fetchInvoiceData,
  authenticate,
  authenticateFromExtension,
  change2FaAuthSetting,
  getLoginPageNotifications,
  emailIsAvailable,
  saveCompanyDetails,
  setNotificationsAsRead,
  saveAccountName,
  savePaypalEmailForAffiliateCommission,
  deleteCustomLogo,
  sendApiPartnershipContactEmail,
  getPayAmountForPartnerApiKeyUsage,
  addSubAccount,
  updateSubAccount,
  deleteSubAccount,
  deleteSubAccounts,
  getSubAccountActivityLogs,
  addDevAccount,
  deleteDevAccount,
  updateDevCallsTopupSetting,
  createAPIAccessKey,
  leaveMainAccount,
  saveEmailSettings,
  changeSiteName,
  changePageName,
  validateSiteDomain,
  createSite,
  createPage,
  createPageRunInBackend,
  generateNewBulkRun,
  updatePageSettings,
  createGuardRunFromSilo,
  updatePagePublishStatus,
  pauseWatchdogSetupForPages,
  cancelWatchdogSetupForPages,
  // updatePagePinStatus,
  createDemoPageRun,
  urlUsedin24Hrs,
  getPageRunStatus,
  markAsReadForRunAutoStatusUpdate,
  getHtmlCountData,
  bulkExcelUploadAndValidate,
  createNewBulkRun,
  cancelBulkRunTask,
  fetchTaskResult,
  submitGoogleSearchTask,
  filterGoogleVariations,
  fetchGoogleSearchLocations,
  downloadExcelReport,
  downloadExcelReportFromUrl,
  downloadInvoiceFile,
  fetchKeywordWizardReports,
  updateKeywordWizardReport,
  deleteKeywordWizardReport,
  downloadKeywordResearchReport,
  updateSupportKeywordReport,
  updatePassword,
  changeEmailRequest,
  getAllTasksForPageRun,
  addOneToCheckList,
  addCustomTask,
  getAllSiteTasks,
  updateCustomTask,
  fetchValidatedCoupons,
  archiveOneFromCheckList,
  restoreOneFromChecklist,
  archiveAllChecklistWithType,
  addAllToCheckList,
  updateChecklistState,
  updateChecklistDueDate,
  updateChecklistAssignee,
  updateChecklistStateFromExtension,
  createChecklistNote,
  deleteChecklistNote,
  updateChecklistNote,
  updateChecklistNoteFromExtension,
  downloadExcelForChecklist,
  downloadTasksForProgressNote,
  downloadDocxForAiWriter,
  getChecklistForExtension,
  getContentBriefNotes,
  createContentBriefNote,
  updateContentBriefNote,
  deleteContentBriefNote,
  updateContentBriefStatus,
  downloadContentBrief,
  getProratedAmountForPurchase,
  makePurchase,
  createPayPalPayment,
  createStripePayment,
  convertSubscription,
  buyNlpEatPackage,
  buyUniversalCreditsPakcage,
  createCreditAutoRefill,
  listCreditAutoRefills,
  updateCreditAutoRefill,
  cancelCreditAutoRefill,
  fetchYearlyUnlimitedConversionDiscount,
  cancelSubscription,
  reactivateSubscription,
  updatePaymentSource,
  setDefaultPaymentSource,
  deletePaymentSource,
  updateCouponCode,
  updatePaypalEmail,
  generateCancellationCode,
  addOpenAiKey,
  manageAiKey,
  testAiKey,
  updateAiModels,
  getAvailableNlpEatPackages,
  getAvailableUniversalPackages,
  getAvailableAPICreditPackages,
  getAvailableWhiteGloveAddonPackages,
  makeNewPageRun,
  loadApiUsageForUser,
  getUsersForAdmin,
  loadAdminComparisonData,
  downloadAllUserExcel,
  downloadAllSales,
  updateUserAccountForAdmin,
  resendRegistrationEmailForAdmin,
  confirmEmailAsValidated,
  adminCreateUserNoCard,
  adminResetUsageLimitForUser,
  adminFairUseStatusForUser,
  adminPauseUsageForUser,
  adminSetFairUseExemptForUser,
  adminCancelSubscription,
  adminCancelCreditAutoRefill,
  getTotalUsers,
  getHistoryForAdmin,
  getKeywordResearchForAdmin,
  loadKeywordResearchUsageForAdmin,
  getSiteContentAuditOrdersForAdmin,
  getSiloOrdersForAdmin,
  getContentOrdersForAdmin,
  getOrdersCommissionsForAdmin,
  getPopAuthorityCustomersForAdmin,
  getAdminMetricsSeries,
  refreshAdminMetrics,
  getAdminMetricsUsers,
  exportAdminMetricsUsers,
  getPopAuthoritySetupFeesForAdmin,
  getPopAuthorityPackageInvoicesForAdmin,
  getPopAuthorityContentOrdersForAdmin,
  getPopAuthorityBoosterOrdersForAdmin,
  startPopAuthorityBoosterDeliveryForAdmin,
  deliverPopAuthorityBoosterForAdmin,
  getPopAuthorityCommissionsForAdmin,
  updatePopAuthorityCommissionForAdmin,
  getPopAuthorityCommissionRates,
  getSerpValidationRecordsForAdmin,
  getSerpValidationRecordForAdmin,
  setSerpValidationVerdictForAdmin,
  getSerpValidationTuningForAdmin,
  updatePopAuthorityCommissionRates,
  getCampaignManagersPercentages,
  updateCampaignManagerPercentage,
  getWatchDogTrackHistoryForAdmin,
  getGuardRunsForUser,
  getAIRunsForUser,
  getLatestAIRunForSite,
  getAIRunsForAdmin,
  getCreditUsageForUser,
  getKeywordResearchCreditUsageForUser,
  getBulkCreditLog,
  getCouponsForAdmin,
  autoGenCouponCode,
  createCouponForAdmin,
  updateCouponForAdmin,
  deleteCouponForAdmin,
  addAliasToCouponForAdmin,
  createAdminUserComment,
  getAllUsersForAdmin,
  getAllNotificationsForAdmin,
  createAdminNotification,
  updateNotificationForAdmin,
  deleteNotificationForAdmin,
  deleteSite,
  deleteSites,
  deletePage,
  deletePages,
  updatePinPageRun,
  deletePageRun,
  deletePageRuns,
  removeTermFromCB,
  recoverTermForCB,
  removeTermsFromCB,
  toggleScoringMethodOfCB,
  setContentBriefCustomWordCount,
  acknowledgeForContentBriefScoreNotChanged,
  getSales,
  getNewUserStats,
  deleteAccount,
  getTotalUsagePerDay,
  getRunsPerDay,
  getAffiliateSales,
  getAffiliatePayouts,
  affiliatePayout,
  removeFromPayout,
  executePayout,
  getPaypalRefunds,
  executePaypalRefund,
  testCancelledWatchdogSetup,
  getUserNotifications,
  deleteUserNotification,
  exportInvoices,
  exportRefundInvoices,
  validatePageAnalysisUrls,
  validateUrlForEditor,
  updateUserSettings,
  saveProjectSettings,
  // updateProjectPinStatus,
  saveRevisionHistoryForContentEditor,
  uploadContentEditorImage,
  saveEditedSourceCode,
  downloadContentEditorZip,
  saveEditedHtml, saveReportsData,
  copyToContentEditor,
  loadSavedSourceCode,
  loadForSpecificRevisionData,
  getWeeklyPageRunStats,
  unsubscribeWatchdogMadeByAdmin,
  openApiTest,
  getPromptForAI,
  getAnswerWithAI,
  getGenerateSchema,
  getRequestSchema,
  getAIAnswerForContentPrompt,
  loadAiRun,
  aiRunChangeCurrentSelection,
  aiRunSaveEditedContent,
  aiRunRemoveContent,
  aiRunAIGeneratedImageCreate,
  aiRunAIGeneratedImageDelete,
  aiRunChangeCurrentSelectionForRichItem,
  aiRunUpdateEditedContentForRichItem,
  deleteAIRun,
  aiRunGetFullHtml,
  createKeywordResearchCampaign,
  getCampaignById,
  updateCampaign,
  connectGoogleAccount,
  disconnectGoogleAccount,
  getGscConnection,
  connectGsc,
  updateGscSettings,
  disconnectGsc,
  listGscProperties,
  getGoogleAccessToken,
  listWinsTrackedUrls,
  addWinsTrackedUrl,
  removeWinsTrackedUrl,
  saveDeliveredUrl,
  startWinsMappingRun,
  getWinsMappingRun,
  listWinsMappingSuggestions,
  approveWinsMappingSuggestion,
  declineWinsMappingSuggestion,
  undoDeclineWinsMappingSuggestion,
  getWinsReportSettings,
  updateWinsReportSettings,
  listWinsPeriods,
  getWinsReport,
  getWinsReportPdf,
  checkWinsData,
  sendWinsReportNow,
  lookupWinsUrl,
  updateWinsTrackedUrl,
  listWinsReportSends,
  getCampaignLatestGeneratedReports,
  getCampaignsForSite,
  createKeywordInsightResearch,
  getKeywordResearchHistoryForCampaign,
  loadKeywordResearchData,
  updateKeywordsVisibility,
  getKeywordsFromTopic,
  clusteringUserKeywordsExcel,
  createSupportKeywordResearch,
  getSupportKeywordResearchHistoryForCampaign,
  loadSupportKeywordResearchData,
  supportKeywordResearch_analyzeClustersWithAI,
  keywordResearchAnalyzeWithAI,
  updateKeywordLabel,
  saveKeywordWizardResult,
  loadRecentKeywordWizardData,
  loadKeywordWizardMonthlySearchAndExtras,
  loadKeywordWizardLabels,
  wizardUpdateKeywordLabel,
  topLevel_getKeywordsFromTopic,
  siloResearchSingle,
  siloResearchSinglePatch,
  siloResearchSingleDelete,
  siloResearchList,
  siloResearchOneClickSiloCreate,
  siloResearchCustomSiloCreate,
  siloResearchCustomSiloStartMetricsTask,
  topLevel_createSupportKeywordResearch,
  topLevel_analyzeClustersWithAI,
  topLevel_getSupportKeywordResearchHistoryForCampaign,
  topLevel_loadSupportKeywordResearchData,
  addKeywordToTrack,
  bulkTrackKeywordToTrack,
  removeKeywordFromTrack,
  updateKeywordTrack,
  getKeywordTrackForCampaign,
  startKeywordTrack,
  getMonthlySearches,
  updateKeywordResearchReport,
  deleteKeywordResearchReport,
  createKeywordGapAnalysis,
  loadKeywordGapHistory,
  loadOneKeywordGapData,
  getProjectsForWorkstation,
  getPagesForWorkstation,
  getPageRunForWorkStation,
  getTaskStatus,
  getActiveTasks,
  acknowledgeTask,
  abortTask,
  updateScoringStrategy,
  updateSummaryReportDay,
  neverMindClicked,
  testRunForSummaryReport,
  updateRecommendationsForPage,
  getDemoUserToken,
  updateSiloKeywordFlag,
  resetBalanceForUser,
  fetchPOPCharges,
  submitPopFeedback,
  addKeywordsToSilo,
  fetchUserHasPayments,
  getFeedbackDownload,
  getFeedbacksForAdmin,
  getFeedbackStatus,
  getReportHeader,
  fetchReportsData,
  getReviewOptimizationOrders,
  createReviewOptimizationOrder,
  updateReviewOptimizationOrder,
  getReviewOptimizationOrderPDFReport,
  getReviewOptimizationOrdersPricingInfo,
  getReviewOptimizationOrdersMetrics,
} from "@/api";
import {
  getPopAuthorityPackages,
  getPopAuthorityCustomQuote,
  getPopAuthoritySiteSubscription,
  getPopAuthoritySubscriptions,
  startPopAuthoritySubscriptionCheckout,
  assignPopAuthoritySubscription,
  markPopAuthoritySubscriptionPaid,
  changePopAuthoritySubscriptionPackage,
  cancelPopAuthoritySubscription,
  undoCancelPopAuthoritySubscription,
  pausePopAuthoritySubscription,
  resumePopAuthoritySubscription,
  closePopAuthoritySite,
  reopenPopAuthoritySite,
  updatePopAuthorityPackageInvoice,
  startPopAuthorityCycleProduction,
  lockPopAuthorityUpcomingCycle,
  getPopAuthorityDeliverables,
  approvePopAuthorityDeliverable,
  requestPopAuthorityDeliverableUpdates,
  approveAllPopAuthorityOrderDeliverables,
  markPopAuthorityDeliverableRevised,
  publishPopAuthorityDeliverable,
  setPopAuthorityDeliverableLiveUrl,
  setPopAuthoritySitePublishingBlocked,
  retryPopAuthoritySitePublishingAccess,
  getPopAuthorityBoosters,
  createPopAuthorityBoosterOrder,
  markPopAuthorityBoosterOrderPaid,
  startPopAuthorityBoosterOrderDelivery,
  deliverPopAuthorityBoosterOrder,
  setPopAuthorityBoosterOrderReportUrl,
  cancelPopAuthorityBoosterOrder,
  updatePopAuthorityBoosterOrder,
  recommendPopAuthorityBooster,
  dismissPopAuthorityBoosterRecommendation,
  recordPopAuthorityReviewCallBookingClick,
  sendPopAuthorityManagerMessage,
} from "@/api";
import { isValidJwt, PageRunQueueItem } from "@/utils";
import router from "@/router";
import { KEYWORDS_INSIGHT_CONFIG } from "../components/PageRun/constants";
import { decodeToken } from "../utils";

Vue.use(Vuex);

const WHITE_GLOVE_ADMIN_MODE_STORAGE_KEY = "isWhiteGloveAdminMode";

/** On unless the user explicitly turned it off (stored "false"); unset defaults to true. */
function readWhiteGloveAdminModeFromStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return true;
    }
    const raw = window.localStorage.getItem(WHITE_GLOVE_ADMIN_MODE_STORAGE_KEY);
    if (raw === "false") return false;
    if (raw === "true") return true;
    return true;
  } catch {
    return true;
  }
}

function writeWhiteGloveAdminModeToStorage(value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(
      WHITE_GLOVE_ADMIN_MODE_STORAGE_KEY,
      value ? "true" : "false"
    );
  } catch {
    // ignore private mode / quota errors
  }
}

const state = {
  // single source of data
  // Review-optimization orders grouped by page_run_id, populated by the batched
  // fetch so per-card buttons read from this cache instead of each firing a request.
  reviewOptimizationOrdersByPageRun: {},
  sites: [],
  pages: [],
  bulkRuns: [],
  pageRuns: [],
  // AI Writer admin Prompt Inspector: LLM prompts accumulated from action
  // responses for the current session (admin-only; deduped by record uid).
  aiwPromptLog: [],
  currentPageRun: {},
  user: {},
  customLogoBase64: "",
  // Seeded from storage so getToken/isAuthenticated are reactive to clearStore
  // in sessions that started from a hard refresh. The router guard's loadUser
  // never commits setJwtToken, so without the seed state.jwt stays "" and
  // clearStore's `state.jwt = ""` is a reactive no-op — isAuthenticated would
  // stay cached as true after logout (App.vue keys PopAssistantButton on it).
  jwt: getToken() || "",
  selectedSubAccountRelation: "", // This is for Sub Account
  selectedDevAccountRelation: "",
  queue: [],
  globalErrorMsg: "",
  allUsers: [],
  pageRunActiveTab: "",
  coupons: [],
  currentSiteId: "",
  currentPageId: "",
  schemaProgresses: {},
  modalData: {},
  // Lock modal of no-plan accounts (see components/Auth/plan-gate.js)
  planRequiredModal: { open: false, reason: "" },

  isUserClosedSideBar: false, // this has sidebar user's change
  isHideSidebar: false, // show/hide sidebar current setting

  adminHistoryRecords: [],
  isLoading: false,
  siloReportProgress: 0,
  notificationsForAdmin: [],
  userInvoices: [],
  menuSearchType: null,
  menuSearchCriteria: "",
  isNewSiloUpdates: false,
  isNewTrackingUpdates: false,
  submittingGoogleSearch: false,
  showGoogleResultsModal: false,
  showAiOverviewFallbackModal: false,
  googleSearchProgress: 1,
  validateUrlsProgress: 1,
  reportsData: null,
  reportsLoading: false,
  reportsError: null,
  reportsHeaderImage: null,

  sortModeSite: "recent",
  sortModePage: "recent",
  sortModePageRun: "recent", //   "recent", "alphabetic", "worstscore", "bestscore"

  // edit html
  isExpressEditor: false,
  seoEditHtmlSource: "",
  seoEditWordCounts: {},
  seoEditEmCounts: {},
  seoEditLsiEmCounts: {},
  seoEditTagCounts: {},
  aiWriterStep: 0,

  // Latest AI Writer / Content Editor screen pushed to Rocky. Lets pop-context
  // (sent from PopAssistantButton, which has no ref to the editor) read the
  // sourceType/activeScreen that don't live in the route URL.
  rockyReadback: {
    screen: null,
    activeScreen: null,
    sourceType: null,
    editorMode: null,
    aiRunId: null,
    pageRunId: null,
  },

  editorArchivedList: [],

  // for cancel button
  currentTaskId: -1,
  currentGoogleCompetitorsTaskId: -1,
  currentGoogleVariationsTaskId: -1,
  fetchedVariations: false,
  originGoogleVariations: [],
  variations: [],
  excludedVariations: [],
  usedGoogleDomain: "",
  googleSearchLocations: [],
  relatedQuestions: [],
  relatedSearches: [],
  insightKeywordsConfig: cloneDeep(KEYWORDS_INSIGHT_CONFIG),
  aiWriterNeedsToSave: false,

  asyncTasks: [],
  clientSideAsyncTasks: [],
  triggerAsyncTasksRefresh: null,  // mutate this to trigger tasks list refresh (the value does not matter)
  isAsyncTaskPopupMinimized: false,

  triggerRockyRefresh: null,  // mutate this to trigger a silent list refresh on the active route (the value does not matter)

  triggerRefreshKeywordResearchCard: null,

  // this is for pagerun strategy
  dashboardType: "contentBrief", // contentBrief / signals
  strategy: "target", // target, adjusted, focus, max
  approach: "regular", // regular, conservative, aggressive, hyper_aggressive

  // this needs updated when new languages get added
  targetLanguages: [
    "english",
    "spanish",
    "french",
    "italian",
    "portuguese",
    // "separator_line",
    "albanian",
    "arabic",
    "armenian",
    "azerbaijani",
    "basque",
    "belarusian",
    "bengali",
    "bulgarian",
    "catalan",
    "chinese",
    "croatian",
    "czech",
    "danish",
    "dutch",
    "estonian",
    "farsi",
    "filipino",
    "finnish",
    "galician",
    "german",
    "greek",
    "hausa",
    "hebrew",
    "hindi",
    "hungarian",
    "icelandic",
    "indonesian",
    "irish",
    "japanese",
    "kazakh",
    "korean",
    "kurdish",
    "latvian",
    "lithuanian",
    "malay",
    "marathi",
    "mongolian",
    "nepali",
    "norwegian",
    "persian",
    "polish",
    "romanian",
    "russian",
    "serbian",
    "slovak",
    "slovenian",
    "swahili",
    "swedish",
    "tagalog",
    "telugu",
    "thai",
    "turkish",
    "ukranian",
    "urdu",
    "vietnamese",
    "yoruba",
  ],
  googleNlpSupportedLanguages: [
    "chinese",
    // "dutch", due to entity analysis is not supported. only supported for content classification
    "english",
    "french",
    "german",
    "italian",
    "japanese",
    "korean",
    "portuguese",
    "russian",
    "spanish",
  ],

  strategies: ["target", "adjusted", "focus", "max", "custom"],
  approachies: ["regular", "conservative", "aggressive", "hyper_aggressive"],
  dashboardTypes: ["contentBrief", "signals"],

  lastReportCreationTime: null,

  currentAIRun: null,

  isDemoTitleDone: false,
  isDemoHeadingsDone: false,
  isDemoMainContentDone: false,

  currentVideoTutorial: null,

  sitesPerPage: 16,
  currentPageForSite: 1,
  pagesPerPage: 12,
  currentPageForPage: 1,
  genSchemaLoading: {},
  selectedSiloKeywords: [],
  process: {},

  currentCampaign: null,
  googleUser: null,

  siloContentReportsOrders: [],
  showSiloContentReportsOrdersCart: false,
  refreshSiloContentReportsOrdersCart: null,  // mutate this to trigger cart data refresh (the value does not matter)
  refreshDataAfterCartPurchase: null,  // mutate this to trigger data refresh after cart purchase (the value does not matter)
  showBalanceTopUpModal: false,
  isWhiteGloveAdminMode: readWhiteGloveAdminModeFromStorage(),
  popCreditCharges: {
    aiGenSchemaCredits: 0,
    aiWriterCreditsPer1000Words: 0,
    aiWriterCreditsFlatRateOwnApiKey: 0,
    googleNlpCredits: 0,
  },
};

const UNLIMITED_SUBSCRIPTION_SERVICES = unlimitedServices;

const AGENCY_PLANS = agencyPlans;

// --- Review-optimization request collapsing --------------------------------
// The page-run report screen renders one ReviewOptimizationButton per card. Each
// button used to fire 3 requests on mount (pricing-info + a paid-count query are
// identical for every card; the orders query is per page run), so a page with N
// runs fired 3N requests, 2N of them duplicates, which could saturate the API and
// return 503s. These module-level caches collapse that burst into ~3 requests by
// (a) deduping the two identical user-level calls and (b) batching the per-run
// orders fetch into a single `pageRunIds` request.
const reviewOptimizationPricingCache = {}; // subAccountRelationId -> { value, promise }
const reviewOptimizationPaidCountCache = {}; // `${subRelId}:${ownerId}` -> { value, promise }
const reviewOptimizationOrdersBatch = {
  ids: new Set(),
  ownerId: null,
  promise: null,
};

const actions = {
  // asynchronous operations
  loadData({ commit, dispatch }) {
    return dispatch("makeHTTPRequest", {
      callback: () => {
        return fetchData().then((response) =>
          commit("setAllData", response.data)
        );
      },
    });
  },
  loadInvoiceData({ dispatch, state }) {
    return dispatch("makeHTTPRequest", {
      callback: () => {
        return fetchInvoiceData(state.user.id);
      },
    });
  },
  loadUser({ commit }, isForIndependentRoutes = false) {
    if (isForIndependentRoutes) {
      const token = getToken();
      commit("setJwtToken", { jwt: token });
      if (!token) {
        return Promise.reject({
          data: { msg: "You are not logged In. Even you don't have token to check" },
        });
      }
    }
    return fetchUser(isForIndependentRoutes);
  },
loadReportsData({ commit }, params = {}) {
  commit('setReportsLoading', true);
  commit('setReportsError', null);

  return fetchReportsData(params)
    .then((response) => {
      // CHANGED: Store the complete response structure
      if (response.data && response.data.status === 'SUCCESS') {
        commit('setReportsData', response.data.data); // Notice: response.data.data
        commit('setReportsHeaderImage', response.data.headerImage); // NEW: Store header image
        commit('setReportsLoading', false);
        return response.data;
      } else {
        throw new Error(response.data?.error || 'Failed to load report data');
      }
    })
    .catch((error) => {
      console.error("Error fetching reports data:", error);
      commit('setReportsError', error.message || 'Failed to fetch reports data');
      commit('setReportsLoading', false);
      throw error;
    });
},
saveEditedData({ commit }, payload) {
  commit('setReportsLoading', true);
  commit('setReportsError', null);

  if (!(payload.siloKeywordId || payload.reviewOptimizationOrderId) || !payload.data) {
    const error = new Error('siloKeywordId or reviewOptimizationOrderId and data are required');
    commit('setReportsError', error.message);
    commit('setReportsLoading', false);
    throw error;
  }

  if (typeof payload.data !== 'object' || Array.isArray(payload.data)) {
    const error = new Error('data must be a JSON object');
    commit('setReportsError', error.message);
    commit('setReportsLoading', false);
    throw error;
  }

  return saveReportsData(payload)
    .then((response) => {
      if (response.data && response.data.status === 'SUCCESS') {
        commit('updateReportsData', payload.data);
        commit('setReportsLoading', false);
        return response.data;
      } else {
        throw new Error(response.data?.error || 'Failed to save report data');
      }
    })
    .catch((error) => {
      console.error("Error saving reports data:", error);
      commit('setReportsError', error.message || 'Failed to save reports data');
      commit('setReportsLoading', false);
      throw error;
    });
},
  updateUser(_, data) {
    return updateUser(data.userId, data.body);
  },
  loadUserHasPaymentSources() {
    return fetchUserHasPayments();
  },
  adminResetBalance({ getters }, user_id) {
    return resetBalanceForUser(getters.getToken, user_id);
  },
  loadSite(_, siteId) {
    return fetchSite(siteId);
  },
  loadSites() {
    return fetchSites();
  },
  updateSite(_, data) {
    return updateSite(data.siteId, data.body);
  },
  updateSitePopImpactScore(_, data) {
    return updateSitePopImpactScore(data.siteId, data.body);
  },
  getSiteContentAudits(_, data) {
    return getSiteContentAudits(data);
  },
  createSiteContentAudit(_, data) {
    return createSiteContentAudit(data.body);
  },
  updateSiteContentAudit(_, data) {
    return updateSiteContentAudit(data.auditId, data.body);
  },
  createBalanceTopUpOrder(_, data) {
    return createBalanceTopUpOrder(data.body);
  },
  updateBalanceTopUpOrder(_, data) {
    return updateBalanceTopUpOrder(data.orderId, data.body);
  },
  getWhiteGloveOrders(_, options = {}) {
    return getWhiteGloveOrders(options);
  },
  getWhiteGloveMissionControlSiteStats(_, { siteId }) {
    return getWhiteGloveMissionControlSiteStats(siteId);
  },
  // POP Authority packages / per-site package subscriptions
  getPopAuthorityPackages() {
    return getPopAuthorityPackages();
  },
  getPopAuthorityCustomQuote(_, data) {
    return getPopAuthorityCustomQuote(data);
  },
  getPopAuthoritySiteSubscription(_, { siteId }) {
    return getPopAuthoritySiteSubscription(siteId);
  },
  getPopAuthoritySubscriptions() {
    return getPopAuthoritySubscriptions();
  },
  startPopAuthoritySubscriptionCheckout(_, { siteId, body }) {
    return startPopAuthoritySubscriptionCheckout(siteId, body);
  },
  assignPopAuthoritySubscription(_, { siteId, body }) {
    return assignPopAuthoritySubscription(siteId, body);
  },
  markPopAuthoritySubscriptionPaid(_, { siteId }) {
    return markPopAuthoritySubscriptionPaid(siteId);
  },
  changePopAuthoritySubscriptionPackage(_, { siteId, body }) {
    return changePopAuthoritySubscriptionPackage(siteId, body);
  },
  cancelPopAuthoritySubscription(_, { siteId, body }) {
    return cancelPopAuthoritySubscription(siteId, body);
  },
  undoCancelPopAuthoritySubscription(_, { siteId }) {
    return undoCancelPopAuthoritySubscription(siteId);
  },
  pausePopAuthoritySubscription(_, { siteId }) {
    return pausePopAuthoritySubscription(siteId);
  },
  resumePopAuthoritySubscription(_, { siteId }) {
    return resumePopAuthoritySubscription(siteId);
  },
  closePopAuthoritySite(_, { siteId, body }) {
    return closePopAuthoritySite(siteId, body);
  },
  reopenPopAuthoritySite(_, { siteId }) {
    return reopenPopAuthoritySite(siteId);
  },
  updatePopAuthorityPackageInvoice(_, { invoiceId, body }) {
    return updatePopAuthorityPackageInvoice(invoiceId, body);
  },
  startPopAuthorityCycleProduction(_, { siteId, cycleNumber }) {
    return startPopAuthorityCycleProduction(siteId, cycleNumber);
  },
  lockPopAuthorityUpcomingCycle(_, { siteId }) {
    return lockPopAuthorityUpcomingCycle(siteId);
  },
  getPopAuthorityDeliverables(_, { siteId }) {
    return getPopAuthorityDeliverables(siteId);
  },
  approvePopAuthorityDeliverable(_, { deliverableId }) {
    return approvePopAuthorityDeliverable(deliverableId);
  },
  requestPopAuthorityDeliverableUpdates(_, { deliverableId, body }) {
    return requestPopAuthorityDeliverableUpdates(deliverableId, body);
  },
  approveAllPopAuthorityOrderDeliverables(_, { orderId }) {
    return approveAllPopAuthorityOrderDeliverables(orderId);
  },
  markPopAuthorityDeliverableRevised(_, { deliverableId }) {
    return markPopAuthorityDeliverableRevised(deliverableId);
  },
  publishPopAuthorityDeliverable(_, { deliverableId, body }) {
    return publishPopAuthorityDeliverable(deliverableId, body);
  },
  setPopAuthorityDeliverableLiveUrl(_, { deliverableId, body }) {
    return setPopAuthorityDeliverableLiveUrl(deliverableId, body);
  },
  setPopAuthoritySitePublishingBlocked(_, { siteId, body }) {
    return setPopAuthoritySitePublishingBlocked(siteId, body);
  },
  retryPopAuthoritySitePublishingAccess(_, { siteId, body }) {
    return retryPopAuthoritySitePublishingAccess(siteId, body);
  },
  // POP Authority booster packs (Phase 8)
  getPopAuthorityBoosters(_, { siteId }) {
    return getPopAuthorityBoosters(siteId);
  },
  createPopAuthorityBoosterOrder(_, { siteId, body }) {
    return createPopAuthorityBoosterOrder(siteId, body);
  },
  markPopAuthorityBoosterOrderPaid(_, { orderId }) {
    return markPopAuthorityBoosterOrderPaid(orderId);
  },
  startPopAuthorityBoosterOrderDelivery(_, { orderId, body }) {
    return startPopAuthorityBoosterOrderDelivery(orderId, body);
  },
  deliverPopAuthorityBoosterOrder(_, { orderId, body }) {
    return deliverPopAuthorityBoosterOrder(orderId, body);
  },
  setPopAuthorityBoosterOrderReportUrl(_, { orderId, body }) {
    return setPopAuthorityBoosterOrderReportUrl(orderId, body);
  },
  cancelPopAuthorityBoosterOrder(_, { orderId, body }) {
    return cancelPopAuthorityBoosterOrder(orderId, body);
  },
  updatePopAuthorityBoosterOrder(_, { orderId, body }) {
    return updatePopAuthorityBoosterOrder(orderId, body);
  },
  recommendPopAuthorityBooster(_, { siteId, body }) {
    return recommendPopAuthorityBooster(siteId, body);
  },
  dismissPopAuthorityBoosterRecommendation(_, { siteId }) {
    return dismissPopAuthorityBoosterRecommendation(siteId);
  },
  recordPopAuthorityReviewCallBookingClick(_, { siteId }) {
    return recordPopAuthorityReviewCallBookingClick(siteId);
  },
  sendPopAuthorityManagerMessage(_, { siteId, body }) {
    return sendPopAuthorityManagerMessage(siteId, body);
  },
  loadBulkRuns() {
    return fetchBulkRuns();
  },
  loadBulkRun(_, runId) {
    return fetchBulkRun(runId);
  },
  deleteBulkRun(_, runId) {
    return deleteBulkRun(runId);
  },
  downloadBulkRunZip(_, data) {
    return downloadBulkRunZip(data);
  },

  loadPages(_, { siteId }) {
    return fetchPages(siteId);
  },
  loadPageRuns(_, { siteId, pageId }) {
    return fetchPageRuns(siteId, pageId);
  },
  loadPageRunsBySearchReportId(_, searchReportId) {
    return fetchPageRunsBySearchReportId(searchReportId);
  },
  loadPageRunsForExtension() {
    return fetchPageRunsForExtension();
  },
  loadPageRunForExtension(_, { pageRunId }) {
    return fetchPageRunForExtension(pageRunId);
  },
  login(context, userData) {
    return authenticate(userData);
  },
  // --- Signup / login redesign (specs/2026-10 - Signup redesign) ---
  signupStart(_, data) {
    return signupStart(data);
  },
  signupVerify(_, data) {
    return signupVerify(data);
  },
  signupResend(_, data) {
    return signupResend(data);
  },
  saveWelcomeAnswers(_, data) {
    return saveWelcomeAnswers(data);
  },
  setPendingFirstRun(_, data) {
    return setPendingFirstRun(data);
  },
  clearPendingFirstRun() {
    return clearPendingFirstRun();
  },
  fetchPlanCatalog() {
    return fetchPlanCatalog();
  },
  previewCoupon(_, data) {
    return previewCoupon(data);
  },
  createCheckoutSession(_, data) {
    return createCheckoutSession(data);
  },
  getCheckoutStatus(_, { sessionId }) {
    return getCheckoutStatus(sessionId);
  },
  fetchFairUsePolicy() {
    return fetchFairUsePolicy();
  },
  loginWithPassword(_, data) {
    return loginWithPassword(data);
  },
  loginConfirmCode(_, data) {
    return loginConfirmCode(data);
  },
  loginResendCode(_, data) {
    return loginResendCode(data);
  },
  requestPasswordReset(_, data) {
    return requestPasswordReset(data);
  },
  validateResetToken(_, { token }) {
    return validateResetToken(token);
  },
  resetPasswordWithToken(_, data) {
    return resetPasswordWithToken(data);
  },
  socialExchange(_, data) {
    return socialExchange(data);
  },
  socialConfirm(_, data) {
    return socialConfirm(data);
  },
  socialLink(_, data) {
    return socialLink(data);
  },
  demo_user_token(_, data) {
    return getDemoUserToken(data);
  },
  loginFromExtension(context, userData) {
    return authenticateFromExtension(userData);
  },
  change2FaAuthSetting(_, data) {
    return change2FaAuthSetting(data);
  },
  getLoginPageNotifications(context) {
    return getLoginPageNotifications();
  },
  emailIsAvailable({ commit }, email) {
    return emailIsAvailable(email);
  },
  saveCompanyDetails(_, companyData) {
    return saveCompanyDetails(companyData);
  },
  setNotificationsAsRead(_, ids) {
    return setNotificationsAsRead(ids);
  },
  saveAccountName(_, data) {
    return saveAccountName(data);
  },
  savePaypalEmailForAffiliateCommission(_, paypalEmail) {
    return savePaypalEmailForAffiliateCommission(paypalEmail);
  },
  deleteCustomLogo() {
    return deleteCustomLogo();
  },
  sendApiPartnershipContactEmail(_, data) {
    return sendApiPartnershipContactEmail(data);
  },
  getPayAmountForPartnerApiKeyUsage() {
    return getPayAmountForPartnerApiKeyUsage();
  },
  addSubAccount(_, data) {
    return addSubAccount(data);
  },
  updateSubAccount(_, data) {
    return updateSubAccount(data);
  },
  deleteSubAccount(_, data) {
    return deleteSubAccount(data);
  },
  deleteSubAccounts(_, data) {
    return deleteSubAccounts(data);
  },
  getSubAccountActivityLogs(_, { params }) {
    return getSubAccountActivityLogs(params);
  },
  addDevAccount(_, data) {
    return addDevAccount(data);
  },
  deleteDevAccount(_, data) {
    return deleteDevAccount(data);
  },
  updateDevCallsTopupSetting(_, data) {
    return updateDevCallsTopupSetting(data);
  },
  createAPIAccessKey({ state }, data) {
    return createAPIAccessKey(data, state.selectedDevAccountRelation.id);
  },
  leaveMainAccount(_, data) {
    return leaveMainAccount(data);
  },
  saveEmailSettings(_, data) {
    return saveEmailSettings(data);
  },
  changeSiteName(_, { id, siteName }) {
    return changeSiteName(id, siteName);
  },
  changePageName(_, { id, pageName }) {
    return changePageName(id, pageName);
  },
  createSite(_, siteData) {
    return createSite(siteData);
  },
  validateSiteDomain(_, url) {
    return validateSiteDomain(url);
  },
  fetchVariations(_, data) {
    return fetchVariations(data.keyword, data.proxyLocation);
  },
  createPage(_, data) {
    return createPage(data);
  },
  createPageRunInBackend(_, data) {
    return createPageRunInBackend(data);
  },

  updatePageSettings(_, data) {
    return updatePageSettings(data);
  },
  createGuardRunFromSilo(_, siloKeywordId) {
    return createGuardRunFromSilo(siloKeywordId);
  },
  updatePagePublishStatus(_, data) {
    return updatePagePublishStatus(data);
  },
  pauseWatchdogSetupForPages(_, data) {
    return pauseWatchdogSetupForPages(data);
  },
  cancelWatchdogSetupForPages(_, data) {
    return cancelWatchdogSetupForPages(data);
  },

  // updatePagePinStatus ({ getters, state }, data) {
  //   return updatePagePinStatus(
  //     getters.getToken,
  //     data,
  //     state.selectedSubAccountRelation.id
  //   );
  // },

  fetchLsaPhrases(_, data) {
    return fetchLsaPhrases(data);
  },
  fetchPageRun(_, { siteId, pageId, pageRunId }) {
    return fetchPageRun(siteId, pageId, pageRunId);
  },
  fetchPageRunBySecretKeyOrPageRunId(_, data) {
    return fetchPageRunBySecretKeyOrPageRunId(data);
  },
  createSecretKey(_, { siteId, pageId, pageRunId }) {
    return createSecretKey(siteId, pageId, pageRunId);
  },
  createDemoPageRun({ getters, state }, data) {
    return createDemoPageRun(data);
  },
  urlUsedin24Hrs({ state }, data) {
    return urlUsedin24Hrs(
      data,
      state.selectedSubAccountRelation.id
    );
  },
  checkStatusOfPageRun(_, { pageRunId }) {
    return getPageRunStatus(pageRunId);
  },
  markAsReadForRunAutoStatusUpdate(_, { pageRunId }) {
    return markAsReadForRunAutoStatusUpdate(pageRunId);
  },
  getHtmlCountData(_, data) {
    return getHtmlCountData(data);
  },
  generateNewBulkRun(_, data) {
    return generateNewBulkRun(data);
  },
  bulkExcelUploadAndValidate(_, xlsxFile) {
    return bulkExcelUploadAndValidate(xlsxFile);
  },
  createNewBulkRun({ commit, dispatch }, data) {
    return createNewBulkRun(omit(data, 'isSilo')).then((response) => {
      let { status, bulkRun } = response.data;

      commit("hideLoading");
      if (data.isSilo) commit("setSiloReportProgress", 1);

      if (status === "SUCCESS") {
        commit("addOrUpdateBulkRun", bulkRun);
        const { taskId } = bulkRun;
        const bulkRunId = bulkRun.id;

        const getBulkRun = () => {
          dispatch("loadBulkRun", bulkRunId).then((res) => {
            if (res.data && res.data.status == "SUCCESS") {
              if (res.data.bulkRun.id) {
                commit("addOrUpdateBulkRun", res.data.bulkRun);
                commit("setUser", res.data.user);
              }
            }
          });
        };

        return new Promise((resolve, reject) => {
          const timer = setInterval(() => {
            return fetchTaskResult(taskId)
              .then((resultResponse) => {
                let { status, value } = resultResponse.data;

                if (status === "SUCCESS") {
                  if (data.isSilo) commit("setSiloReportProgress", 0);
                  clearInterval(timer);
                  getBulkRun();
                  resolve(resultResponse);
                } else if (status === "REVOKED") {
                  if (data.isSilo) commit("setSiloReportProgress", 0);
                  getBulkRun();
                  resolve(resultResponse);
                  if (state.isCalculatingBulkNow) {
                    clearInterval(timer);
                  }
                } else if (status === "FAILURE") {
                  if (data.isSilo) commit("setSiloReportProgress", 0);
                  clearInterval(timer);
                  getBulkRun();
                  reject(resultResponse);
                } else if (status === "PROGRESS") {
                  if (data.isSilo) commit("setSiloReportProgress", value);
                  // load updated bulk runs again
                  getBulkRun();
                }
              })
              .catch((e) => {
                console.log("error in interval timer ", e);
                clearInterval(timer);
                reject({ status: "FAILURE" });
              });
          }, 6000);
        });
      } else {
        return response;
      }
    });
  },
  cancelBulkRunTask(_, taskId) {
    return cancelBulkRunTask(taskId);
  },
  syncPageRunsQueue({ state, commit }, { siteId, pageId }) {
    return new Promise((resolve, reject) => {
      // first, cleanup queue (only keep listeners of loaded pageruns)
      [...state.queue].forEach((q) => {
        if (!state.pageRuns.find((pageRun) => q.pageRunId === pageRun.id)) {
          commit("deQueuePageRunItem", {
            siteId: q.siteId,
            pageId: q.pageId,
            pageRunId: q.pageRunId,
          });
        }
      })

      // second, create listeners for never seen pending pageruns
      state.pageRuns.forEach((pageRun) => {
        if (pageRun.status === "PENDING") {
          if (!state.queue.find((q) => q.pageRunId === pageRun.id)) {
            commit("enQueuePageRunItem", {
              siteId: parseInt(siteId),
              pageId: parseInt(pageId),
              pageRunId: pageRun.id,
            });
          }
        }
      });
      resolve("ok");
    });
  },
  makeHTTPRequest({ getters, commit }, { callback }) {
    try {
      if (getters.isAuthenticated) {
        return callback().catch((err) => {
          console.log("****makeHTTPRequest callback error ", err);
          if (err.response.data && !err.response.data.authenticated) {
            commit("setJwtToken", "");
            commit("setGlobalErrorMsg", { msg: "Please reauthenticate" });
          }
          return 1;
        });
      }
      return 1;
    } catch (err) {
      console.log("****Error making http request", err);
      return 1;
    }
  },
  downloadExcelReport({ getters }, { filePath, secretKey }) {
    return downloadExcelReport(getters.getToken, filePath, { secretKey });
  },
  downloadInvoiceFile({ getters }, { fileName }) {
    return downloadInvoiceFile(getters.getToken, fileName);
  },
  downloadExcelReportFromUrl({ getters }, { url }) {
    return downloadExcelReportFromUrl(getters.getToken, url);
  },
  fetchKeywordWizardReports(_, campaignId) {
    return fetchKeywordWizardReports(campaignId);
  },
  updateKeywordWizardReport(_, { data }) {
    return updateKeywordWizardReport(data);
  },
  deleteKeywordWizardReport(_, { reportId }) {
    return deleteKeywordWizardReport(reportId);
  },
  updateKeywordResearchReport(_, { data }) {
    return updateKeywordResearchReport(data);
  },
  deleteKeywordResearchReport(_, { reportId }) {
    return deleteKeywordResearchReport(reportId);
  },
  downloadKeywordResearchReport({ getters }, { reportId, tool }) {
    return downloadKeywordResearchReport(getters.getToken, reportId, tool);
  },
  updateSupportKeywordReport(_, { data }) {
    return updateSupportKeywordReport(data);
  },
  updatePassword(_, data) {
    return updatePassword(data);
  },
  changeEmailRequest(_, data) {
    return changeEmailRequest(data);
  },
  getAllTasksForPageRun(_, data) {
    return getAllTasksForPageRun(data);
  },
  addOneToCheckList(_, data) {
    return addOneToCheckList(data);
  },
  addCustomTask(_, data) {
    return addCustomTask(data);
  },
  getAllSiteTasks(_, data) {
    return getAllSiteTasks(data);
  },
  updateCustomTask(_, { taskId, ...data }) {
    return updateCustomTask(taskId, data);
  },
  archiveOneFromCheckList(_, data) {
    return archiveOneFromCheckList(data);
  },
  restoreOneFromChecklist(_, data) {
    return restoreOneFromChecklist(data);
  },
  archiveAllChecklistWithType(_, data) {
    return archiveAllChecklistWithType(data);
  },
  addAllToCheckList(_, data) {
    return addAllToCheckList(data);
  },
  updateChecklistState(_, data) {
    return updateChecklistState(data);
  },
  updateChecklistDueDate(_, data) {
    return updateChecklistDueDate(data);
  },
  updateChecklistAssignee(_, data) {
    return updateChecklistAssignee(data);
  },
  updateChecklistStateFromExtension(_, data) {
    return updateChecklistStateFromExtension(data);
  },
  createChecklistNote(_, data) {
    return createChecklistNote(data);
  },
  updateChecklistNote(_, data) {
    return updateChecklistNote(data);
  },
  updateChecklistNoteFromExtension(_, data) {
    return updateChecklistNoteFromExtension(data);
  },
  deleteChecklistNote(_, noteId) {
    return deleteChecklistNote(noteId);
  },
  downloadExcelForChecklist(_, data) {
    return downloadExcelForChecklist(data);
  },
  downloadTasksForProgressNote(_, data) {
    return downloadTasksForProgressNote(data);
  },
  downloadDocxForAiWriter({ getters }, data) {
    return downloadDocxForAiWriter(getters.getToken, data);
  },
  getChecklistForExtension(_, data) {
    return getChecklistForExtension(data);
  },
  getContentBriefNotes(_, data) {
    return getContentBriefNotes(data);
  },
  createContentBriefNote(_, data) {
    return createContentBriefNote(data);
  },
  updateContentBriefNote(_, data) {
    return updateContentBriefNote(data);
  },
  deleteContentBriefNote(_, noteId) {
    return deleteContentBriefNote(noteId);
  },
  updateContentBriefStatus(_, data) {
    return updateContentBriefStatus(data);
  },
  downloadContentBrief(_, data) {
    return downloadContentBrief(data);
  },
  getProratedAmountForPurchase({ state }, data) {
    return getProratedAmountForPurchase(state.user.id, data);
  },
  createPurchase({ state }, data) {
    return makePurchase(state.user.id, data);
  },
  createPayPalPayment(_, data) {
    return createPayPalPayment(data);
  },
  createStripePayment(_, data) {
    return createStripePayment(data);
  },
  convertSubscription() {
    return convertSubscription();
  },
  buyNlpEatPackage(_, data) {
    return buyNlpEatPackage(data);
  },
  buyUniversalCreditsPakcage(_, data) {
    return buyUniversalCreditsPakcage(data);
  },
  createCreditAutoRefill(_, data) {
    return createCreditAutoRefill(data);
  },
  listCreditAutoRefills() {
    return listCreditAutoRefills();
  },
  updateCreditAutoRefill(_, { refillId, ...data }) {
    return updateCreditAutoRefill(refillId, data);
  },
  cancelCreditAutoRefill(_, { refillId }) {
    return cancelCreditAutoRefill(refillId);
  },
  executePayPalSubscription({ getters }, data) {
    //http://localhost:8080/execute-subscription?token=EC-1T983725F19521617
    return this.executePayPalSubscription(getters.getToken, data);
  },
  fetchYearlyUnlimitedConversionDiscount({ state }) {
    return fetchYearlyUnlimitedConversionDiscount(state.user.id);
  },
  cancelSubscription({ state }, data) {
    return cancelSubscription(state.user.id, data);
  },
  updateUserSettings({ state }, data) {
    return updateUserSettings(data);
  },
  reactivateSubscription({ state }, data) {
    return reactivateSubscription(state.user.id, data);
  },
  addCoupon({ state }, data) {
    return updateCouponCode(state.user.id, data);
  },
  updatePaypalEmail(_, data) {
    return updatePaypalEmail(data);
  },
  generateCancellationCode() {
    return generateCancellationCode();
  },
  addOpenAiKey(_, data) {
    return addOpenAiKey(data);
  },
  manageAiKey(_, data) {
    return manageAiKey(data);
  },
  testAiKey(_, data) {
    return testAiKey(data);
  },
  updateAiModels(_, data) {
    return updateAiModels(data);
  },
  getAvailableNlpEatPackages({ getters }) {
    return getAvailableNlpEatPackages(getters.getToken);
  },
  getAvailableUniversalPackages() {
    return getAvailableUniversalPackages();
  },
  getAvailableAPICreditPackages() {
    return getAvailableAPICreditPackages();
  },
  getAvailableWhiteGloveAddonPackages() {
    return getAvailableWhiteGloveAddonPackages();
  },
  rerunAnalysis(_, data) {
    return makeNewPageRun(data);
  },
  loadApiUsageForUser({ state }, data) {
    return loadApiUsageForUser(
      data,
      state.selectedDevAccountRelation.id
    );
  },
  loadUsersForAdmin(_, { params }) {
    return getUsersForAdmin(params);
  },
  loadAdminComparisonData(
    _,
    {
      primaryStartDate,
      primaryEndDate,
      secondaryStartDate,
      secondaryEndDate,
      source,
      aggregator,
    }
  ) {
    return loadAdminComparisonData(
      primaryStartDate,
      primaryEndDate,
      secondaryStartDate,
      secondaryEndDate,
      source,
      aggregator
    );
  },
  downloadAllUserExcel(_, data) {
    return downloadAllUserExcel(data);
  },
  downloadAllSales() {
    return downloadAllSales();
  },
  updateUserAccountForAdmin(_, data) {
    return updateUserAccountForAdmin(data);
  },
  resendRegistrationEmailForAdmin(_, data) {
    return resendRegistrationEmailForAdmin(data);
  },
  adminCreateUserNoCard(_, data) {
    return adminCreateUserNoCard(data);
  },
  confirmEmailAsValidated(_, data) {
    return confirmEmailAsValidated(data);
  },
  adminResetUsageLimitForUser(_, data) {
    return adminResetUsageLimitForUser(data);
  },
  adminFairUseStatusForUser(_, userId) {
    return adminFairUseStatusForUser(userId);
  },
  adminPauseUsageForUser(_, data) {
    return adminPauseUsageForUser(data);
  },
  adminSetFairUseExemptForUser(_, data) {
    return adminSetFairUseExemptForUser(data);
  },
  adminCancelSubscription(_, data) {
    return adminCancelSubscription(data);
  },
  adminCancelCreditAutoRefill(_, data) {
    return adminCancelCreditAutoRefill(data);
  },
  loadCouponsForAdmin(_, { params }) {
    return getCouponsForAdmin(params);
  },
  makeCouponForAdmin(_, data) {
    return createCouponForAdmin(data);
  },
  autoGenCouponCode(_, data) {
    return autoGenCouponCode(data);
  },
  updateCouponForAdmin(_, data) {
    return updateCouponForAdmin(data);
  },
  deleteCouponForAdmin(_, { id }) {
    return deleteCouponForAdmin(id);
  },
  addAliasToCouponForAdmin(_, data) {
    return addAliasToCouponForAdmin(data);
  },
  makeAdminUserComment(_, data) {
    return createAdminUserComment(data);
  },
  removeSite(_, { siteId }) {
    return deleteSite(siteId);
  },
  removeSites(_, { siteIds }) {
    return deleteSites(siteIds);
  },
  removePage(_, { siteId, pageId }) {
    return deletePage(siteId, pageId);
  },
  removePages(_, { siteId, pageIds }) {
    return deletePages(siteId, pageIds);
  },
  updatePinPageRun(_, data) {
    return updatePinPageRun(data);
  },
  removePageRun(_, { siteId, pageId, pageRunId }) {
    return deletePageRun(siteId, pageId, pageRunId);
  },
  removePageRuns(_, { siteId, pageId, pageRunIds }) {
    return deletePageRuns(siteId, pageId, pageRunIds);
  },
  removeTermFromCB(_, data) {
    return removeTermFromCB(data);
  },
  recoverTermForCB(_, data) {
    return recoverTermForCB(data);
  },
  removeTermsFromCB(_, data) {
    return removeTermsFromCB(data);
  },
  toggleScoringMethodOfCB(_, data) {
    return toggleScoringMethodOfCB(data);
  },
  setContentBriefCustomWordCount(_, data) {
    return setContentBriefCustomWordCount(data);
  },
  acknowledgeForContentBriefScoreNotChanged(_, data) {
    return acknowledgeForContentBriefScoreNotChanged(data);
  },
  loadHistoryForAdmin(_, { params }) {
    return getHistoryForAdmin(params);
  },
  loadKeywordResearchForAdmin(_, { params }) {
    return getKeywordResearchForAdmin(params);
  },
  loadKeywordResearchUsageForAdmin(_, { params }) {
    return loadKeywordResearchUsageForAdmin(params);
  },
  submitPopFeedbackAction(_, payload) {
    return submitPopFeedback(payload);
  },
loadFeedbackBacklinksForAdmin(_, { params }) {
    return getFeedbacksForAdmin(params);
  },
  loadFeedbackStatus() {
    return getFeedbackStatus();
  },
  loadFeedbackDownload({ getters }) {
  return getFeedbackDownload(getters.getToken).then(response => {
    const blob = new Blob([response.data], { type: 'text/csv' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', 'feedback_export.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  })
},
  getSiteContentAuditOrdersForAdmin(_, { params }) {
    return getSiteContentAuditOrdersForAdmin(params);
  },
  getSiloOrdersForAdmin(_, { params }) {
    return getSiloOrdersForAdmin(params);
  },
  getContentOrdersForAdmin(_, { params }) {
    return getContentOrdersForAdmin(params);
  },
  getOrdersCommissionsForAdmin(_, { params }) {
    return getOrdersCommissionsForAdmin(params);
  },
  // Admin > Products > POP Authority
  getPopAuthorityCustomersForAdmin(_, { params }) {
    return getPopAuthorityCustomersForAdmin(params);
  },
  getAdminMetricsSeries() {
    return getAdminMetricsSeries();
  },
  refreshAdminMetrics() {
    return refreshAdminMetrics();
  },
  getAdminMetricsUsers(_, { params }) {
    return getAdminMetricsUsers(params);
  },
  exportAdminMetricsUsers(_, { params }) {
    return exportAdminMetricsUsers(params);
  },
  getPopAuthoritySetupFeesForAdmin(_, { params }) {
    return getPopAuthoritySetupFeesForAdmin(params);
  },
  getPopAuthorityPackageInvoicesForAdmin(_, { params }) {
    return getPopAuthorityPackageInvoicesForAdmin(params);
  },
  getPopAuthorityContentOrdersForAdmin(_, { params }) {
    return getPopAuthorityContentOrdersForAdmin(params);
  },
  getPopAuthorityBoosterOrdersForAdmin(_, { params }) {
    return getPopAuthorityBoosterOrdersForAdmin(params);
  },
  startPopAuthorityBoosterDeliveryForAdmin(_, { orderId }) {
    return startPopAuthorityBoosterDeliveryForAdmin(orderId);
  },
  deliverPopAuthorityBoosterForAdmin(_, { orderId, body }) {
    return deliverPopAuthorityBoosterForAdmin(orderId, body);
  },
  getSerpValidationRecordsForAdmin(_, { params }) {
    return getSerpValidationRecordsForAdmin(params);
  },
  getSerpValidationRecordForAdmin(_, { recordId }) {
    return getSerpValidationRecordForAdmin(recordId);
  },
  setSerpValidationVerdictForAdmin(_, { recordId, body }) {
    return setSerpValidationVerdictForAdmin(recordId, body);
  },
  getSerpValidationTuningForAdmin(_, { params }) {
    return getSerpValidationTuningForAdmin(params);
  },
  getPopAuthorityCommissionsForAdmin(_, { params }) {
    return getPopAuthorityCommissionsForAdmin(params);
  },
  updatePopAuthorityCommissionForAdmin(_, { orderType, orderId, body }) {
    return updatePopAuthorityCommissionForAdmin(orderType, orderId, body);
  },
  getPopAuthorityCommissionRates() {
    return getPopAuthorityCommissionRates();
  },
  updatePopAuthorityCommissionRates(_, { body }) {
    return updatePopAuthorityCommissionRates(body);
  },
  getCampaignManagersPercentages() {
    return getCampaignManagersPercentages();
  },
  updateCampaignManagerPercentage(_, data) {
    return updateCampaignManagerPercentage(data.key, data.body);
  },
  getWatchDogTrackHistoryForAdmin(_, { params }) {
    return getWatchDogTrackHistoryForAdmin(params);
  },
  getGuardRunsForUser(_, { params }) {
    return getGuardRunsForUser(params);
  },
  getAIRunsForUser(_, params) {
    return getAIRunsForUser(params);
  },
  getLatestAIRunForSite(_, params) {
    return getLatestAIRunForSite(params);
  },
  getAIRunsForAdmin(_, { params }) {
    return getAIRunsForAdmin(params);
  },
  getCreditUsageForUser(_, { params }) {
    return getCreditUsageForUser(params);
  },
  getKeywordResearchCreditUsageForUser(_, { params }) {
    return getKeywordResearchCreditUsageForUser(params);
  },
  getBulkCreditLog(_, { params }) {
    return getBulkCreditLog(params);
  },
  updatePageRunNote(_, { siteId, pageId, pageRunId, note }) {
    return updatePageRunNote({ siteId, pageId, pageRunId, note });
  },
  fetchValidatedCoupons(_, { promoCode, isRegisterPage }) {
    return fetchValidatedCoupons(promoCode, isRegisterPage);
  },
  fetchAvailableProxies() {
    return fetchProxies();
  },
  fetchSalesData(_, { startDate, endDate }) {
    return getSales(startDate, endDate);
  },
  fetchNewUserStats(_, { startDate, endDate }) {
    return getNewUserStats(startDate, endDate);
  },
  fetchRunsPerDay(_, { startDate, endDate }) {
    return getRunsPerDay(startDate, endDate);
  },
  fetchTotalUsagePerDay({ getters }, { days }) {
    return getTotalUsagePerDay(getters.getToken, days);
  },
  fetchTotalUsersForAdmin() {
    return getTotalUsers();
  },
  fetchAffiliateSales() {
    return getAffiliateSales();
  },
  fetchAffiliatePayouts() {
    return getAffiliatePayouts();
  },
  executeAffiliatePayout(_, data) {
    return affiliatePayout(data);
  },
  removeFromPayout(_, data) {
    return removeFromPayout(data);
  },
  executePayout(_, data) {
    return executePayout(data);
  },
  fetchPaypalRefunds() {
    return getPaypalRefunds();
  },
  executePaypalRefund(_, data) {
    return executePaypalRefund(data);
  },
  testCancelledWatchdogSetup(_, data) {
    return testCancelledWatchdogSetup(data);
  },
  deleteUserAccount() {
    return deleteAccount();
  },
  fetchAllUsersForAdmin() {
    return getAllUsersForAdmin();
  },
  fetchAllNotificationsForAdmin(_, { params }) {
    return getAllNotificationsForAdmin(params);
  },
  createNotification(_, notification) {
    return createAdminNotification(notification);
  },
  fetchUserNotifications(_, { userId }) {
    return getUserNotifications(userId);
  },
  deleteUserNotification(_, { userId, notificationId }) {
    return deleteUserNotification(userId, notificationId);
  },
  updateNotificationForAdmin(_, notification) {
    return updateNotificationForAdmin(notification);
  },
  deleteNotificationForAdmin(_, { id }) {
    return deleteNotificationForAdmin(id);
  },
  createStripeCardAdditionLink({ state }) {
    return createStripeCardAdditionLink(state.user.id);
  },
  fetchPaymentSources({ state }) {
    return fetchPaymentSources(state.user.id);
  },
  updatePaymentSource({ getters, state }, { cardId, stripeData }) {
    return updatePaymentSource(
      getters.getToken,
      state.user.id,
      cardId,
      stripeData
    );
  },
  setDefaultPaymentSource({ state }, cardOrPaymentId) {
    return setDefaultPaymentSource(state.user.id, cardOrPaymentId);
  },
  deletePaymentSource({ state }, { cardId }) {
    return deletePaymentSource(state.user.id, cardId);
  },
  downloadInvoices({ state }, data) {
    const userId = data.userId == null ? state.user.id : data.userId;
    return exportInvoices(userId, {
      invoices: data.invoices,
      isPaid: data.isPaid,
    });
  },
  downloadRefundInvoices({ state }, data) {
    const userId = data.userId == null ? state.user.id : data.userId;
    return exportRefundInvoices(userId, {
      refunds: data.refunds,
    });
  },
  fetchGoogleSearchLocations({ commit }) {
    return fetchGoogleSearchLocations().then((response) => {
      commit("setGoogleSearchLocations", response.data);
      return response.data;
    });
  },
  submitGoogleSearchTask({ state, commit, dispatch }, data) {
    const { keyword, secondaryKeywords } = data;
    commit("setGoogleSearchProgress", 1);
    return submitGoogleSearchTask(data).then((response) => {
      // let { status, taskId } = response.data
      const { status, competitorTaskId, variationsTaskId } = response.data;
      commit("setCurrentGoogleCompetitorsTaskId", competitorTaskId);
      commit("setCurrentGoogleVariationsTaskId", variationsTaskId);

      if (status !== "FAILURE") {
        let variationsTimer = null;
        try {
          variationsTimer = setInterval(() => {
            return fetchTaskResult(variationsTaskId)
              .then((resultResponse) => {
                let { status: variationsStatus } = resultResponse.data;
                if (variationsTaskId != state.currentGoogleVariationsTaskId) {
                  clearInterval(variationsTimer);
                } else if (variationsStatus === "SUCCESS") {
                  clearInterval(variationsTimer);

                  let variations = resultResponse.data.results.variations;

                  commit("setFetchedVariations", true);
                  commit("setOriginGoogleVariations", variations);
                  // commit("setVariations", variations);
                  // commit(
                  //   "setExcludedVariations",
                  //   resultResponse.data.results.excludedVariations
                  // );
                  let gDomain = resultResponse.data.results.gDomain;
                  commit("setUsedGoogleDomain", gDomain);

                  // fetched variations - need to set current variations in store
                  // and somehow let rest of app know variations have been set

                  dispatch("filterGoogleVariations", {
                    keyword: keyword,
                    secondaryKeywords: secondaryKeywords,
                    variations: variations,
                    gDomain: gDomain,
                  });
                } else if (variationsStatus === "FAILURE") {
                  clearInterval(variationsTimer);
                }
              })
              .catch((e) => {
                console.log("gv error ", e);
                clearInterval(variationsTimer);
              });
          }, 8000);
        } catch (e) {
          console.log("gv error", e);
          if (variationsTimer) {
            clearInterval(variationsTimer);
          }
        }
      }

      return new Promise((resolve, reject) => {
        let competitorsTimer = null;
        try {
          if (status === "FAILURE") {
            reject(response.data);
          } else if (status === "SUCCESS") {
            competitorsTimer = setInterval(() => {
              return fetchTaskResult(competitorTaskId)
                .then((resultResponse) => {
                  let { status: competitorsStatus } = resultResponse.data;
                  if (
                    competitorTaskId != state.currentGoogleCompetitorsTaskId
                  ) {
                    clearInterval(competitorsTimer);
                    resolve({ status: "CANCELLED" });
                  } else if (competitorsStatus === "SUCCESS") {
                    clearInterval(competitorsTimer);
                    resultResponse.data.proxyId = response.data.proxyId;

                    commit(
                      "setRelatedQuestions",
                      resultResponse.data.results.hasOwnProperty(
                        "relatedQuestions"
                      )
                        ? resultResponse.data.results.relatedQuestions
                        : []
                    );
                    commit(
                      "setRelatedSearches",
                      resultResponse.data.results.hasOwnProperty(
                        "relatedSearches"
                      )
                        ? resultResponse.data.results.relatedSearches
                        : []
                    );
                    resolve(resultResponse.data);
                  } else if (competitorsStatus === "FAILURE") {
                    clearInterval(competitorsTimer);
                    reject(resultResponse.data);
                  } else if (competitorsStatus === "PROGRESS") {
                    let currentProgress = parseInt(state.googleSearchProgress);
                    currentProgress =
                      currentProgress < 99
                        ? currentProgress + 1
                        : currentProgress;
                    let progressUpdate = parseInt(resultResponse.data.value);
                    if (currentProgress < progressUpdate) {
                      currentProgress = progressUpdate;
                    }
                    commit("setGoogleSearchProgress", currentProgress);
                  }
                })
                .catch((e) => {
                  console.log("gc tasks error ", e);
                  clearInterval(competitorsTimer);
                  reject({ status: "FAILURE" });
                });
            }, 500);
          }
        } catch (e) {
          console.log("gc tasks error ", e);
          if (competitorsTimer) {
            clearInterval(competitorsTimer);
          }
        }
      });
    });
  },
  filterGoogleVariations(
    { state, commit },
    { keyword, secondaryKeywords, variations }
  ) {
    return filterGoogleVariations(
      keyword,
      secondaryKeywords,
      variations
    ).then((response) => {
      if (response.data.status == "SUCCESS") {
        commit("setVariations", response.data.variations);
        let excludedVariations = state.excludedVariations
          .slice()
          .concat(response.data.excludedVariations);
        commit("addExcludedVariations", excludedVariations);
      }
    });
  },
  validatePageAnalysisUrls({ commit, state }, data) {
    // commit('showLoading')
    commit("setValidateUrlsProgress", 1);
    return validatePageAnalysisUrls(data).then((response) => {
      return new Promise((resolve, reject) => {
        if (response.data.status === "FAILURE") {
          reject(response.data);
        } else {
          const timer = setInterval(() => {
            const { taskId } = response.data;
            return fetchTaskResult(taskId)
              .then((resultResponse) => {
                const { status } = resultResponse.data;
                if (status === "SUCCESS") {
                  clearInterval(timer);
                  // commit('hideLoading')
                  resultResponse.data.proxyId = response.data.proxyId;
                  resolve(resultResponse.data);
                } else if (status === "FAILURE") {
                  clearInterval(timer);
                  // commit('hideLoading')
                  resolve({ status: "FAILURE" });
                } else if (status === "PROGRESS" || status == "PENDING") {
                  let currentProgress = parseInt(state.validateUrlsProgress);
                  currentProgress =
                    currentProgress < 97
                      ? currentProgress + 3
                      : currentProgress;
                  let progressUpdate = 1;
                  try {
                    progressUpdate = parseInt(resultResponse.data.value);
                  } catch (e) {
                    console.log(e);
                  }
                  if (currentProgress < progressUpdate) {
                    currentProgress = progressUpdate;
                  }
                  commit("setValidateUrlsProgress", currentProgress);
                }
              })
              .catch((err) => {
                console.log(err);
                clearInterval(timer);
                // commit('hideLoading')
                resolve({ status: "FAILURE" });
              });
          }, 5000); //was 10000
        }
      });
    });
  },
  validateUrlForEditor(_, data) {
    return validateUrlForEditor(data);
  },
  setMenuSearchType({ commit }, searchType) {
    return commit("setMenuSearchType", searchType);
  },
  setMenuSearchCriteria({ commit }, searchCriteria) {
    return commit("setMenuSearchCriteria", searchCriteria);
  },
  saveProjectSettings(_, data) {
    return saveProjectSettings(data);
  },
  // updateProjectPinStatus({ getters, commit }, data) {
  //   return updateProjectPinStatus(getters.getToken, data);
  // },
  saveRevisionHistoryForContentEditor(_, data) {
    return saveRevisionHistoryForContentEditor(data);
  },
  uploadContentEditorImage({ state }, data) {
    return uploadContentEditorImage(
      data.imageFile,
      data,
      state.selectedSubAccountRelation?.id
    );
  },
  saveEditedSourceCode(_, data) {
    return saveEditedSourceCode(data);
  },
    saveEditedHtml({ getters, commit }, data) {
    return saveEditedHtml(
      getters.getToken,
      data,
      state.selectedSubAccountRelation?.id
    );
  },
  loadSavedSourceCode(_, data) {
    return loadSavedSourceCode(data);
  },
  copyToContentEditor(_, data) {
    return copyToContentEditor(data);
  },
  downloadContentEditorZip({ getters }, data) {
    return downloadContentEditorZip(
      getters.getToken,
      data,
      state.selectedSubAccountRelation?.id
    );
  },
  loadForSpecificRevisionData(_, data) {
    return loadForSpecificRevisionData(data);
  },
  getWeeklyPageRunStats(state) {
    return getWeeklyPageRunStats();
  },
  unsubscribeWatchdogMadeByAdmin({ getters }, data) {
    return unsubscribeWatchdogMadeByAdmin(data);
  },
  updateScoringStrategy(_, data) {
    return updateScoringStrategy(data);
  },
  updateSummaryReportDay({ state }, data) {
    return updateSummaryReportDay(data);
  },
  neverMindClicked({ state }) {
    return neverMindClicked();
  },
  testRunForSummaryReport({ state }) {
    return testRunForSummaryReport();
  },
  updateRecommendationsForPage(_, data) {
    return updateRecommendationsForPage(data);
  },
  // Test api endpoint
  openApiTest({ getters }, data) {
    return openApiTest(getters.getToken, data);
  },
  getPromptForAI(_, data) {
    return getPromptForAI(data);
  },
  getGenerateSchema(_, data) {
    return getGenerateSchema(data);
  },
  getRequestSchema(_, data) {
    return getRequestSchema(data);
  },
  getAnswerWithAI(_, data) {
    return getAnswerWithAI(data);
  },
  getAIAnswerForContentPrompt(_, data) {
    return getAIAnswerForContentPrompt(data);
  },
  loadAiRun(_, data) {
    return loadAiRun(data);
  },
  aiRunChangeCurrentSelection(_, data) {
    return aiRunChangeCurrentSelection(data);
  },
  aiRunSaveEditedContent(_, data) {
    return aiRunSaveEditedContent(data);
  },
  aiRunRemoveContent(_, data) {
    return aiRunRemoveContent(data);
  },
  aiRunChangeCurrentSelectionForRichItem(_, data) {
    return aiRunChangeCurrentSelectionForRichItem(data);
  },
  aiRunUpdateEditedContentForRichItem(_, data) {
    return aiRunUpdateEditedContentForRichItem(data);
  },
  aiRunAIGeneratedImageCreate(_, data) {
    return aiRunAIGeneratedImageCreate(data);
  },
  aiRunAIGeneratedImageDelete(_, data) {
    return aiRunAIGeneratedImageDelete(data);
  },
  deleteAIRun(_, data) {
    return deleteAIRun(data);
  },

  aiRunGetFullHtml(_, data) {
    return aiRunGetFullHtml(data);
  },

  createKeywordResearchCampaign(_, data) {
    return createKeywordResearchCampaign(data);
  },
  getCampaignById(_, campaignId) {
    return getCampaignById(campaignId);
  },
  updateCampaign(_, { campaignId, body }) {
    return updateCampaign(campaignId, body);
  },
  connectGoogleAccount(_, body) {
    return connectGoogleAccount(body);
  },
  disconnectGoogleAccount() {
    return disconnectGoogleAccount();
  },
  getGscConnection(_, siteId) {
    return getGscConnection(siteId);
  },
  connectGsc(_, { siteId, body }) {
    return connectGsc(siteId, body);
  },
  updateGscSettings(_, { siteId, body }) {
    return updateGscSettings(siteId, body);
  },
  disconnectGsc(_, siteId) {
    return disconnectGsc(siteId);
  },
  listGscProperties(_, siteId) {
    return listGscProperties(siteId);
  },
  getGoogleAccessToken() {
    return getGoogleAccessToken();
  },
  listWinsTrackedUrls(_, siteId) {
    return listWinsTrackedUrls(siteId);
  },
  addWinsTrackedUrl(_, { siteId, body }) {
    return addWinsTrackedUrl(siteId, body);
  },
  removeWinsTrackedUrl(_, trackedUrlId) {
    return removeWinsTrackedUrl(trackedUrlId);
  },
  saveDeliveredUrl(_, { siloKeywordId, body }) {
    return saveDeliveredUrl(siloKeywordId, body);
  },
  startWinsMappingRun(_, siteId) {
    return startWinsMappingRun(siteId);
  },
  getWinsMappingRun(_, siteId) {
    return getWinsMappingRun(siteId);
  },
  listWinsMappingSuggestions(_, siteId) {
    return listWinsMappingSuggestions(siteId);
  },
  approveWinsMappingSuggestion(_, suggestionId) {
    return approveWinsMappingSuggestion(suggestionId);
  },
  declineWinsMappingSuggestion(_, suggestionId) {
    return declineWinsMappingSuggestion(suggestionId);
  },
  undoDeclineWinsMappingSuggestion(_, suggestionId) {
    return undoDeclineWinsMappingSuggestion(suggestionId);
  },
  getWinsReportSettings(_, siteId) {
    return getWinsReportSettings(siteId);
  },
  updateWinsReportSettings(_, { siteId, body }) {
    return updateWinsReportSettings(siteId, body);
  },
  listWinsPeriods(_, siteId) {
    return listWinsPeriods(siteId);
  },
  getWinsReport(_, { siteId, period }) {
    return getWinsReport(siteId, period);
  },
  listWinsReportSends(_, siteId) {
    return listWinsReportSends(siteId);
  },
  checkWinsData(_, siteId) {
    return checkWinsData(siteId);
  },
  sendWinsReportNow(_, { siteId, body }) {
    return sendWinsReportNow(siteId, body);
  },
  lookupWinsUrl(_, { siteId, url }) {
    return lookupWinsUrl(siteId, url);
  },
  updateWinsTrackedUrl(_, { trackedUrlId, body }) {
    return updateWinsTrackedUrl(trackedUrlId, body);
  },
  getWinsReportPdf(_, { siteId, period }) {
    return getWinsReportPdf(siteId, period);
  },
  getCampaignLatestGeneratedReports(_, campaignId) {
    return getCampaignLatestGeneratedReports(campaignId);
  },
  getCampaignsForSite(_, siteId) {
    return getCampaignsForSite(siteId);
  },

  createKeywordInsightResearch(_, data) {
    return createKeywordInsightResearch(data);
  },
  getKeywordResearchHistoryForCampaign(_, data) {
    return getKeywordResearchHistoryForCampaign(data);
  },
  loadKeywordResearchData(_, data) {
    return loadKeywordResearchData(data);
  },
  updateKeywordsVisibility(_, data) {
    return updateKeywordsVisibility(data);
  },
  keywordResearchAnalyzeWithAI(_, data) {
    return keywordResearchAnalyzeWithAI(data);
  },
  updateKeywordLabel(_, data) {
    return updateKeywordLabel(data);
  },
  getKeywordsFromTopic(_, data) {
    return getKeywordsFromTopic(data);
  },
  clusteringUserKeywordsExcel(_, excelFile) {
    return clusteringUserKeywordsExcel(excelFile);
  },
  createSupportKeywordResearch(_, data) {
    return createSupportKeywordResearch(data);
  },
  getSupportKeywordResearchHistoryForCampaign(_, data) {
    return getSupportKeywordResearchHistoryForCampaign(data);
  },
  loadSupportKeywordResearchData(_, data) {
    return loadSupportKeywordResearchData(data);
  },
  supportKeywordResearch_analyzeClustersWithAI(_, data) {
    return supportKeywordResearch_analyzeClustersWithAI(data);
  },
  saveKeywordWizardResult(_, data) {
    return saveKeywordWizardResult(data);
  },
  loadRecentKeywordWizardData(_, data) {
    return loadRecentKeywordWizardData(data);
  },
  loadKeywordWizardMonthlySearchAndExtras(_, data) {
    return loadKeywordWizardMonthlySearchAndExtras(data);
  },
  loadKeywordWizardLabels(_, data) {
    return loadKeywordWizardLabels(data);
  },
  wizardUpdateKeywordLabel(_, data) {
    return wizardUpdateKeywordLabel(data);
  },
  siloResearchSingle(_, data) {
    return siloResearchSingle(data);
  },
  siloResearchSinglePatch(_, data) {
    return siloResearchSinglePatch(data);
  },
  siloResearchSingleDelete(_, data) {
    return siloResearchSingleDelete(data);
  },
  siloResearchList(_, data) {
    return siloResearchList(data);
  },
  siloResearchOneClickSiloCreate(_, data) {
    return siloResearchOneClickSiloCreate(data);
  },
  siloResearchCustomSiloCreate(_, data) {
    return siloResearchCustomSiloCreate(data);
  },
  siloResearchCustomSiloStartMetricsTask(_, data) {
    return siloResearchCustomSiloStartMetricsTask(data);
  },
  topLevel_getKeywordsFromTopic(_, data) {
    return topLevel_getKeywordsFromTopic(data);
  },
  topLevel_createSupportKeywordResearch(_, data) {
    return topLevel_createSupportKeywordResearch(data);
  },
  topLevel_analyzeClustersWithAI(_, data) {
    return topLevel_analyzeClustersWithAI(data);
  },
  topLevel_getSupportKeywordResearchHistoryForCampaign(_, data) {
    return topLevel_getSupportKeywordResearchHistoryForCampaign(data);
  },
  topLevel_loadSupportKeywordResearchData(_, data) {
    return topLevel_loadSupportKeywordResearchData(data);
  },
  addKeywordToTrack(_, data) {
    return addKeywordToTrack(data);
  },
  bulkTrackKeywordToTrack(_, data) {
    return bulkTrackKeywordToTrack(data);
  },
  removeKeywordFromTrack(_, data) {
    return removeKeywordFromTrack(data);
  },
  updateKeywordTrack(_, data) {
    return updateKeywordTrack(data);
  },
  getKeywordTrackForCampaign(_, campaignId) {
    return getKeywordTrackForCampaign(campaignId);
  },
  startKeywordTrack(_, data) {
    return startKeywordTrack(data);
  },
  getMonthlySearches(_, data) {
    return getMonthlySearches(data);
  },
  askForHelpWithKeywords(_, data) {
    return askForHelpWithKeywords(data.body);
  },
  getSilos(_, data) {
    return getSilos(data);
  },
  getSilo(_, siloId) {
    return getSilo(siloId);
  },
  deleteSilo(_, siloId) {
    return deleteSilo(siloId);
  },
  bulkDeleteSilos(_, data) {
    return bulkDeleteSilos(data.body);
  },
  updateSilo(_, data) {
    return updateSilo(data.siloId, data.body);
  },
  moveSiloKeywords(_, data) {
    return moveSiloKeywords(data.siloId, data.body);
  },
  getSiloKeywords(_, data) {
    return getSiloKeywords(data);
  },
  getSiloKeyword(_, data) {
    return getSiloKeyword(data);
  },
  getSiloPDFReport({ state }, data) {
    return getSiloPDFReport(data, state.selectedSubAccountRelation?.id);
  },
  getSiloWordReport({ state }, data) {
    return getSiloWordReport(data, state.selectedSubAccountRelation?.id);
  },
  getReportHeader({ state }, data) {
    return getReportHeader(data, state.selectedSubAccountRelation?.id);
  },
  addKeywordToSilo(_, data) {
    return addKeywordToSilo(data.siloId, data.body);
  },
  addKeywordsToSilo(_, data) {
    return addKeywordsToSilo(data.siloId, data.body);
  },
  removeKeywordFromSilo(_, keywordId) {
    return removeKeywordFromSilo(keywordId);
  },
  restoreSiloKeyword(_, keywordId) {
    return restoreSiloKeyword(keywordId);
  },
  bulkRemoveKeywordFromSilo(_, data) {
    return bulkRemoveKeywordFromSilo(data);
  },
  bulkRestoreSiloKeyword(_, data) {
    return bulkRestoreSiloKeyword(data);
  },
  updateSiloKeyword(_, data) {
    return updateSiloKeyword(data.siloKeywordId, data.body);
  },
  createSilo(_, data) {
    return createSilo(data);
  },
  getAvailableSiloContentReportPackages() {
    return getAvailableSiloContentReportPackages();
  },
  getAvailableSiloContentReportDeliveryOptionsForSite(_, data) {
    return getAvailableSiloContentReportDeliveryOptionsForSite(data);
  },
  getSiloContentReportOrders(_, data) {
    return getSiloContentReportOrders(data);
  },
  updateSiloContentReportOrder(_, data) {
    return updateSiloContentReportOrder(data.orderId, data.body);
  },
  getCartItems(_, data) {
    return getCartItems(data);
  },
  getCartInvoices(_, data) {
    return getCartInvoices(data);
  },
  closeCartInvoice(_, data) {
    return closeCartInvoice(data.cartInvoiceId);
  },
  setCartItemsAsPaid() {
    return setCartItemsAsPaid();
  },
  createKeywordGapAnalysis(_, data) {
    return createKeywordGapAnalysis(data);
  },
  loadKeywordGapHistory(_, data) {
    return loadKeywordGapHistory(data);
  },
  loadOneKeywordGapData(_, data) {
    return loadOneKeywordGapData(data);
  },
  getProjectsForWorkstation(_, params) {
    return getProjectsForWorkstation(params);
  },
  getPagesForWorkstation(_, data) {
    return getPagesForWorkstation(data);
  },
  getPageRunForWorkStation(_, data) {
    return getPageRunForWorkStation(data);
  },

  getTaskStatus(_, taskId) {
    return getTaskStatus(taskId);
  },
  getActiveTasks(_, data) {
    return getActiveTasks(data);
  },
  acknowledgeTask(_, data) {
    return acknowledgeTask(data);
  },
  abortTask(_, data) {
    return abortTask(data);
  },
  updateSiloKeywordFlag(_, data) {
    return updateSiloKeywordFlag(data);
  },
  fetchPOPCharges({ getters }) {
    return fetchPOPCharges(getters.getToken);
  },
  getReviewOptimizationOrders({ getters }, data) {
    return getReviewOptimizationOrders(
      getters.getToken,
      data,
      state.selectedSubAccountRelation.id
    );
  },
  createReviewOptimizationOrder({ getters, state }, data) {
    return createReviewOptimizationOrder(getters.getToken, data.body, state.selectedSubAccountRelation.id);
  },
  updateReviewOptimizationOrder({ getters, state }, data) {
    return updateReviewOptimizationOrder(getters.getToken, data.orderId, data.body, state.selectedSubAccountRelation.id);
  },
  getReviewOptimizationOrderPDFReport({ getters }, data) {
    return getReviewOptimizationOrderPDFReport(getters.getToken, data, state.selectedSubAccountRelation?.id);
  },
  getReviewOptimizationOrdersPricingInfo({ getters }) {
    return getReviewOptimizationOrdersPricingInfo(
      getters.getToken,
      state.selectedSubAccountRelation.id
    );
  },
  // Debounced batch loader: every ReviewOptimizationButton that mounts in the same
  // tick registers its page run id here, then a single `pageRunIds` request fetches
  // them all and stores the results grouped by page_run_id.
  ensureReviewOptimizationOrders({ getters, commit }, { ownerId, pageRunId }) {
    reviewOptimizationOrdersBatch.ids.add(pageRunId);
    reviewOptimizationOrdersBatch.ownerId = ownerId;
    if (!reviewOptimizationOrdersBatch.promise) {
      reviewOptimizationOrdersBatch.promise = new Promise((resolve) => {
        setTimeout(() => {
          const ids = [...reviewOptimizationOrdersBatch.ids];
          const owner = reviewOptimizationOrdersBatch.ownerId;
          reviewOptimizationOrdersBatch.ids = new Set();
          reviewOptimizationOrdersBatch.ownerId = null;
          reviewOptimizationOrdersBatch.promise = null;
          getReviewOptimizationOrders(
            getters.getToken,
            { user_id: owner, pageRunIds: ids.join(',') },
            state.selectedSubAccountRelation.id
          )
            .then((response) => {
              const grouped = {};
              if (response.data && response.data.status === 'SUCCESS') {
                (response.data.data || []).forEach((record) => {
                  if (!grouped[record.page_run_id]) grouped[record.page_run_id] = [];
                  grouped[record.page_run_id].push(record);
                });
              }
              commit('mergeReviewOptimizationOrdersByPageRun', grouped);
            })
            .finally(() => resolve());
        }, 0);
      });
    }
    return reviewOptimizationOrdersBatch.promise;
  },
  // Deduped pricing-info fetch (identical for every card on the screen). Keyed by
  // sub-account relation so switching sub-accounts refetches. `force` invalidates
  // the cached value (used by the targeted refresh after creating an order).
  loadReviewOptimizationPricingInfoCached({ getters }, { force = false } = {}) {
    const key = String(state.selectedSubAccountRelation.id);
    const cache = reviewOptimizationPricingCache[key] || (reviewOptimizationPricingCache[key] = {});
    if (force) cache.value = undefined;
    if (cache.value !== undefined) return Promise.resolve(cache.value);
    if (cache.promise) return cache.promise;
    cache.promise = getReviewOptimizationOrdersPricingInfo(
      getters.getToken,
      state.selectedSubAccountRelation.id
    )
      .then((response) => {
        if (response.data && response.data.status === 'SUCCESS') {
          cache.value = response.data.next_price;
        }
        cache.promise = null;
        return cache.value;
      })
      .catch((error) => {
        cache.promise = null;
        throw error;
      });
    return cache.promise;
  },
  // Deduped paid-orders count (identical for every card on the screen).
  loadReviewOptimizationPaidCountCached({ getters }, { ownerId, force = false } = {}) {
    const key = `${state.selectedSubAccountRelation.id}:${ownerId}`;
    const cache = reviewOptimizationPaidCountCache[key] || (reviewOptimizationPaidCountCache[key] = {});
    if (force) cache.value = undefined;
    if (cache.value !== undefined) return Promise.resolve(cache.value);
    if (cache.promise) return cache.promise;
    cache.promise = getReviewOptimizationOrders(
      getters.getToken,
      { user_id: ownerId, order_status: 'paid', paginate: 1, page: 1, perPage: 1 },
      state.selectedSubAccountRelation.id
    )
      .then(({ data }) => {
        cache.value = (data && data.total) || 0;
        cache.promise = null;
        return cache.value;
      })
      .catch((error) => {
        cache.promise = null;
        throw error;
      });
    return cache.promise;
  },
  getReviewOptimizationOrdersMetrics({ getters }) {
    return getReviewOptimizationOrdersMetrics(
      getters.getToken,
      state.selectedSubAccountRelation.id
    );
  },
};

const mutations = {
  // isolated data mutations
  mergeReviewOptimizationOrdersByPageRun(state, grouped) {
    state.reviewOptimizationOrdersByPageRun = {
      ...state.reviewOptimizationOrdersByPageRun,
      ...grouped,
    };
  },
  setReviewOptimizationOrdersForPageRun(state, { pageRunId, orders }) {
    state.reviewOptimizationOrdersByPageRun = {
      ...state.reviewOptimizationOrdersByPageRun,
      [pageRunId]: orders,
    };
  },
  setProjects(state, projects) {
    state.projects = projects;
  },
  // AI Writer Prompt Inspector — append newly-seen LLM-call records (deduped by
  // uid; the same completed-tick prompts can be re-returned by later polls).
  AIW_APPEND_PROMPTS(state, calls) {
    if (!Array.isArray(calls) || !calls.length) return;
    const seen = new Set(state.aiwPromptLog.map((c) => c.uid));
    const fresh = calls.filter((c) => c && c.uid && !seen.has(c.uid));
    if (fresh.length) state.aiwPromptLog = state.aiwPromptLog.concat(fresh);
  },
  AIW_CLEAR_PROMPTS(state) {
    state.aiwPromptLog = [];
  },
  setSiloReportProgress(state, newVal) {
    state.siloReportProgress = newVal;
  },
  incrementSiloReportProgress(state, increment) {
    state.siloReportProgress = (state.siloReportProgress || 0) + increment;
  },
  setInsightKeywordsConfig(state, payload) {
    state.insightKeywordsConfig[payload.key] = payload.value;
  },
  resetInsightKeywords(state) {
    state.insightKeywordsConfig = cloneDeep(KEYWORDS_INSIGHT_CONFIG);
  },
  setIsAsyncTaskPopupMinimized(state, value) {
    state.isAsyncTaskPopupMinimized = value;
  },
  setAsyncTasks(state, payload) {
    state.asyncTasks = payload;
  },
  resetAsyncTasks(state) {
    state.asyncTasks = [];
  },
  triggerAsyncTasksRefresh(state) {
    state.triggerAsyncTasksRefresh = !state.triggerAsyncTasksRefresh;
  },
  triggerRockyRefresh(state) {
    state.triggerRockyRefresh = !state.triggerRockyRefresh;
  },
  triggerRefreshKeywordResearchCard(state) {
    state.triggerRefreshKeywordResearchCard = !state.triggerRefreshKeywordResearchCard;
  },
    setReportsData(state, data) {
    state.reportsData = data;
  },
  setReportsLoading(state, isLoading) {
    state.reportsLoading = isLoading;
  },
  setReportsError(state, error) {
    state.reportsError = error;
  },
setReportsHeaderImage(state, headerImage) {
  state.reportsHeaderImage = headerImage;
}, updateReportsData(state, data) {
  state.reportsData = { ...state.reportsData, ...data };
},
  registerNewClientSideAsyncTask(state, payload) {
    state.clientSideAsyncTasks = [
      ...state.clientSideAsyncTasks,
      {
        'is_client_side': true,
        'id': payload.id,
        'type': payload.type,
        'record_id': payload.record_id,
        'task_status': {
          'status': 'PROGRESS',
          'msg': payload.task_status.msg,
          'value': payload.task_status.value
        },
        'initiated_at': (new Date()).toISOString().replace('T', ' ').split('.')[0],
        'is_pending_or_in_progress': true,
        'is_completed': 0,
        'is_acknowledged_by_user': 0,
      }
    ];
  },
  updateClientSideAsyncTaskStatus(state, payload) {
    state.clientSideAsyncTasks = state.clientSideAsyncTasks.map((task) => {
      if (task.id !== payload.id) {
        return task
      }
      if (payload.task_status.status === 'PROGRESS' && task.is_pending_or_in_progress) {
        return {
          ...task,
          'task_status': {
            ...task.task_status,
            ...payload.task_status
          },
          'is_pending_or_in_progress': true,
          'is_completed': 0,
        }
      } else if (payload.task_status.status === 'SUCCESS') {
        return {
          ...task,
          'task_status': {
            ...task.task_status,
            ...payload.task_status
          },
          'is_pending_or_in_progress': false,
          'is_completed': 1,
        }
      } else if (payload.task_status.status === 'FAILURE') {
        return {
          ...task,
          'task_status': {
            ...task.task_status,
            ...payload.task_status
          },
          'is_pending_or_in_progress': false,
          'is_completed': 0,
        }
      } else {
        return task
      }
    });
  },
  removeClientSideAsyncTask(state, targetId) {
    state.clientSideAsyncTasks = state.clientSideAsyncTasks.filter(task => task.id !== targetId)
  },
  setDemoStepDone(state, key) {
    state[key] = true;
  },
  setGlobalErrorMsg(state, { msg }) {
    state.globalErrorMsg = msg;
  },
  setAiWriterLoading(state, isLoading) {
    state.aiWriterLoading = isLoading;
  },
  setAiWriterStep(state, step) {
    state.aiWriterStep = step;
  },
  setRockyReadback(state, payload) {
    state.rockyReadback = { ...state.rockyReadback, ...payload };
  },
  clearRockyReadback(state) {
    state.rockyReadback = {
      screen: null,
      activeScreen: null,
      sourceType: null,
      editorMode: null,
      aiRunId: null,
      pageRunId: null,
    };
  },
  setAiWritingData(state, payload) {
    state.aiWriterData = payload;
  },
  setGenerateSchema(state, { schemaType, isLoading }) {
    state.genSchemaLoading[schemaType] = isLoading;
    state.genSchemaLoading = {
      ...state.genSchemaLoading,
      [schemaType]: isLoading,
    };
  },
  toggleSelectedSiloKeyword(state, id) {
    const newArr = cloneDeep(state.selectedSiloKeywords);
    const idx = newArr.findIndex(keywordId => keywordId === id);
    if (idx === -1) newArr.push(id);
    else newArr.splice(idx, 1)
    state.selectedSiloKeywords = newArr;
  },
  resetSelectedSiloKeywords(state) {
    state.selectedSiloKeywords = [];
  },
  selectAllSiloKeywords(state, ids) {
    state.selectedSiloKeywords = ids;
  },
  setProgress(state, { schemaType, value }) {
    state.process = { ...state.process, [schemaType]: value };
  },
  setSchemaProgress(state, { schemaType, pageRunId, value }) {
    const cpState = cloneDeep(state.schemaProgresses);
    const key = `${pageRunId}>${schemaType}`;
    cpState[key] = value || (cpState[key] || 0) + 5;

    state.schemaProgresses = cpState;
  },
  setPopUpModal(state, { pageRunId, currentLocation, value, schemaType }) {
    const cloneState = cloneDeep(state.modalData);
    state.modalData = {
      ...cloneState,
      pageRunId,
      currentLocation,
      value:
        state.pageRunActiveTab === `schemaType-${pageRunId}` ? false : value,
      schemaType,
    };
  },
  setInvoices(state, { invoices }) {
    state.userInvoices = invoices
      .slice()
      .filter((invoice) => invoice.charge !== 0)
      .sort((a, b) => a.created - b.created);
  },
  setAllData(state, payload) {
    state.user = payload;
    state.sites = payload.sites;
  },
  setSites(state, sites) {
    state.sites = sites;
  },
  setBulkRuns(state, bulkRuns) {
    state.bulkRuns = bulkRuns;
  },
  addOrUpdateBulkRun(state, bulkRun) {
    let isExist = false;
    for (let i = 0; i < state.bulkRuns.length; i++) {
      if (state.bulkRuns[i].id == bulkRun.id) {
        isExist = true;
        break;
      }
    }
    if (isExist) {
      state.bulkRuns = state.bulkRuns.map((run) => {
        if (run.id == bulkRun.id) {
          return bulkRun;
        }
        return run;
      });
    } else {
      state.bulkRuns = [bulkRun].concat(state.bulkRuns);
    }
  },
  addSite(state, site) {
    const sites = state.sites.slice();
    sites.push(site);
    state.sites = sites;
  },
  addPage(state, page) {
    const sites = state.sites.slice();
    const idx = sites.findIndex((s) => s.id === page.siteId);
    sites[idx].pages.push(page);
    state.sites = sites;
  },
  setPage(state, page) {
    const sites = state.sites.slice();
    const idx = sites.findIndex((s) => s.id === page.siteId);
    sites[idx].pages = sites[idx].pages.map((p) => {
      if (p.id == page.id) {
        return page;
      } else {
        return p;
      }
    });
    state.sites = sites;
  },
  setPageRuns(state, pageRuns) {
    state.pageRuns = pageRuns;
  },
  setAiRuns(state, aiRuns) {
    state.aiRuns = aiRuns;
  },
  addPageRun(state, pageRun) {
    const pageRuns = state.pageRuns.slice();
    let found = pageRuns.find((p) => p.id == pageRun.id);
    if (!found) {
      pageRuns.push(pageRun);
      state.pageRuns = pageRuns;
    } else {
      state.pageRuns = pageRuns.map((item) => {
        return item.id == pageRun.id ? pageRun : item;
      });
    }
  },
  updatePageRun(state, pageRun) {
    const pageRuns = state.pageRuns.slice();
    for (let i = 0; i < pageRuns.length; i++) {
      if (pageRun.id == pageRuns[i].id) {
        pageRuns[i] = pageRun;
      }
    }
    state.pageRuns = pageRuns;
  },
  setUser(state, user) {
    state.user = user;
  },
  showPlanRequiredModal(state, payload) {
    state.planRequiredModal = { open: true, reason: (payload && payload.reason) || "" };
  },
  hidePlanRequiredModal(state) {
    state.planRequiredModal = { open: false, reason: "" };
  },
  setUserHasPaymentSources(state, hasPaymentSource) {
    state.user.hasPaymentSource = hasPaymentSource;
  },
  setCustomLogoBase64(state, logoBase64) {
    state.customLogoBase64 = logoBase64;
  },
  setUserData(state, payload) {
    state.user = payload.userData;
  },
  setUserSettings(state, settings) {
    const user = Object.assign({}, state.user, { settings });
    state.user = user;
  },
  setJwtToken(state, payload) {
    if (payload && payload.hasOwnProperty("jwt")) {
      state.jwt = payload.jwt;
      if (payload?.is_demo) {
        sessionStorage.setItem("token", payload.jwt);
        localStorage.removeItem("token")
      } else {
        localStorage.setItem("token", payload.jwt);
        sessionStorage.removeItem("token");
      }
    } else {
      state.jwt = "";
      sessionStorage.removeItem("token");
      localStorage.removeItem("token");
    }
  },
  setCurrentSubAccount(state, payload) {
    state.selectedSubAccountRelation = payload;
    if (!payload?.isMissionControlSubAccount) {
      state.isWhiteGloveAdminMode = false;
    } else {
      state.isWhiteGloveAdminMode = readWhiteGloveAdminModeFromStorage();
    }
  },
  setCurrentDevAccount(state, payload) {
    state.selectedDevAccountRelation = payload;
  },
  setSite(state, payload) {
    const siteIdx = state.sites.findIndex((site) => site.id === payload.id);
    if (siteIdx > -1) {
      let sites = state.sites.map((site) => site);
      sites[siteIdx] = payload;
      state.sites = sites;
    } else {
      state.sites.push(payload);
    }
  },
  setCurrentSiteById(state, { siteId }) {
    state.currentSiteId = siteId;
    state.currentPageId = "";
  },
  setCurrentPageById(state, { pageId }) {
    state.currentPageId = pageId;
  },
  setIsUserClosedSideBar(state, payload) {
    state.isUserClosedSideBar = payload;
  },
  setHideSideBar(state, payload) {
    state.isHideSidebar = payload;
  },
  setCurrentPage(state, payload) {
    state.currentPage = payload;
  },
  setCurrentPageRun(state, payload) {
    state.currentPageRun = payload;
  },
  enQueuePageRunItem(state, { siteId, pageId, pageRunId }) {
    const queue = state.queue.slice();
    queue.push(new PageRunQueueItem(this, siteId, pageId, pageRunId));
    state.queue = queue;
  },
  deQueuePageRunItem(state, { siteId, pageId, pageRunId }) {
    const itemIdx = state.queue.findIndex(
      (item) =>
        item.siteId === siteId &&
        item.pageId === pageId &&
        item.pageRunId === pageRunId
    );
    if (state.queue[itemIdx]) {
      if (state.queue[itemIdx]?.timer) {
        clearInterval(state.queue[itemIdx].timer)
      }
    }
    state.queue = state.queue.filter(item => (
      !(
        item.siteId === siteId &&
        item.pageId === pageId &&
        item.pageRunId === pageRunId
      )
    ))
  },
  setAllUsers(state, { users }) {
    state.allUsers = users;
  },
  setPageRunActiveTab(state, tab) {
    state.pageRunActiveTab = tab;
  },
  updateUser(state, { user }) {
    const users = state.allUsers.slice();
    const idx = users.findIndex((u) => u.id === user.id);
    if (idx > -1) {
      users[idx] = user;
    }
    state.allUsers = users;
  },
  /**
   * An admin changed an account's type in this session (White Glove -> POP Authority...): the
   * screens pick their family from the account type cached with the user payload, so refresh it
   * where this session holds it: the session user itself and the account relations (mission
   * control viewing that account). Updated in place, so the selected relation stays the same
   * object and nothing reloads; the family getters recompute.
   */
  applyAccountTypeChange(state, { userId, accountType }) {
    if (!userId || !accountType) return;
    if (state.user && state.user.id === userId) {
      state.user.accountType = accountType;
    }
    const relations = [
      ...((state.user && state.user.ownerAccounts) || []),
      ...(state.selectedSubAccountRelation && typeof state.selectedSubAccountRelation === "object"
        ? [state.selectedSubAccountRelation] : []),
    ];
    relations.forEach((relation) => {
      if (relation && relation.ownerId === userId) {
        relation.ownerAccountType = accountType;
      }
    });
  },
  setCoupons(state, { coupons }) {
    state.coupons = coupons;
  },
  setAdminHistory(state, { historyRecords }) {
    state.adminHistoryRecords = historyRecords;
  },
  showLoading(state) {
    state.isLoading = true;
  },
  hideLoading(state) {
    state.isLoading = false;
  },
  setAiWriterNeedsToSave(state, value) {
    state.aiWriterNeedsToSave = value;
  },
  setSortModeSites(state, mode) {
    state.sortModeSite = mode;
  },
  setSortModePages(state, mode) {
    state.sortModePage = mode;
  },
  setSortModePageruns(state, mode) {
    state.sortModePageRun = mode;
  },
  setSeoSourceCode(state, source) {
    state.seoEditHtmlSource = source;
  },
  setEditorType(state, value) {
    state.isExpressEditor = value;
  },
  setEditedHtmlCountData(
    state,
    { wordCounts, emCounts, lsiEmCounts, tagCounts }
  ) {
    state.seoEditWordCounts = wordCounts;
    state.seoEditEmCounts = emCounts;
    state.seoEditLsiEmCounts = lsiEmCounts;
    state.seoEditTagCounts = tagCounts;
  },
  addEditorArchivedItem(state, value) {
    let found = state.editorArchivedList.find((item) => {
      return item.signal == value.signal && item.type == value.type;
    });
    if (!found) {
      state.editorArchivedList.push(value);
    }
  },
  clearEditorArchivedList(state) {
    state.editorArchivedList = [];
  },
  clearStore(state) {
    localStorage.removeItem("token");
    localStorage.removeItem("lastSelectedSubAccountRelationId");
    state.sites = [];
    state.pages = [];
    state.pageRuns = [];
    state.currentPageRun = {};
    state.user = {};
    state.jwt = "";
    state.selectedSubAccountRelation = "";
    state.queue = [];
    state.globalErrorMsg = "";
    state.allUsers = [];
    state.coupons = [];
    state.currentSiteId = "";
    state.adminHistoryRecords = [];
    state.isLoading = false;
    state.aiWriterNeedsToSave = false;
    state.notificationsForAdmin = [];
    state.userInvoices = [];
    state.menuSearchType = null;
    state.menuSearchCriteria = "";
    state.isWhiteGloveAdminMode = false;
    writeWhiteGloveAdminModeToStorage(false);
  },
  setWhiteGloveAdminMode(state, value) {
    const next = Boolean(value);
    state.isWhiteGloveAdminMode = next;
    writeWhiteGloveAdminModeToStorage(next);
  },
  setMenuSearchType(state, searchType) {
    state.menuSearchType = searchType;
  },
  setMenuSearchCriteria(state, searchCriteria) {
    state.menuSearchCriteria = searchCriteria;
  },
  setIsNewSiloUpdates(state, value) {
    state.isNewSiloUpdates = value;
  },
  setIsNewTrackingUpdates(state, value) {
    state.isNewTrackingUpdates = value;
  },
  setSubmittingGoogleSearch(state, value) {
    state.submittingGoogleSearch = value;
    state.currentTaskId = -1;
  },
  setShowGoogleResultsModal(state, value) {
    state.showGoogleResultsModal = value;
  },
  setShowAiOverviewFallbackModal(state, value) {
    state.showAiOverviewFallbackModal = value;
  },
  setGoogleSearchProgress(state, value) {
    state.googleSearchProgress = value;
  },
  setValidateUrlsProgress(state, value) {
    state.validateUrlsProgress = value;
  },
  // setCurrentTaskId(state, value) {
  //   state.currentTaskId = value;
  // },
  setCurrentGoogleCompetitorsTaskId(state, value) {
    state.currentGoogleCompetitorsTaskId = value;
  },
  setCurrentGoogleVariationsTaskId(state, value) {
    state.currentGoogleVariationsTaskId = value;
  },
  setFetchedVariations(state, value) {
    state.fetchedVariations = value;
  },
  setVariations(state, value) {
    state.variations = value;
  },
  setOriginGoogleVariations(state, value) {
    state.originGoogleVariations = value;
  },
  resetVariations(state) {
    state.variations = [];
    state.fetchedVariations = false;
  },
  resetRelatedQuestionsAndSearches(state) {
    state.originGoogleVariations = [];
    state.relatedQuestions = [];
    state.relatedSearches = [];
  },
  setRelatedQuestions(state, value) {
    state.relatedQuestions = value;
  },
  setRelatedSearches(state, value) {
    state.relatedSearches = value;
  },
  setExcludedVariations(state, value) {
    state.excludedVariations = value;
  },
  setUsedGoogleDomain(state, value) {
    state.usedGoogleDomain = value;
  },
  addExcludedVariations(state, value) {
    state.excludedVariations = [...state.excludedVariations, ...value];
  },
  setGoogleSearchLocations(state, value) {
    value.splice(5, 0, "Exact Location (Chrome Extension)");
    state.googleSearchLocations = value;
  },
  setDashboardType(state, payload) {
    state.dashboardType = payload;
  },
  setStrategy(state, strategy) {
    state.strategy = strategy;
  },
  setApproach(state, approach) {
    state.approach = approach;
  },
  updateLastReportCreationTime(state) {
    state.lastReportCreationTime = Date.now();
  },
  setCurrentAIRun(state, payload) {
    state.currentAIRun = payload;
  },

  setCurrentVideoTutorialToDisplay(state, payload) {
    state.currentVideoTutorial = payload;
  },
  setCurrentPageForSite(state, pageNum) {
    state.currentPageForSite = pageNum;
  },
  setCurrentPageForPage(state, pageNum) {
    state.currentPageForPage = pageNum;
  },

  setCurrentCampaign(state, c) {
    state.currentCampaign = c;
  },
  updateGoogleUser(state, u) {
    state.googleUser = u;
  },
  setShowSiloContentReportsOrdersCart(state, payload) {
    state.showSiloContentReportsOrdersCart = payload;
  },
  setShowBalanceTopUpModal(state, payload) {
    state.showBalanceTopUpModal = payload;
  },
  triggerRefreshSiloContentReportsOrdersCart(state, payload) {
    state.refreshSiloContentReportsOrdersCart = payload;
  },
  triggerRefreshDataAfterCartPurchase(state, payload) {
    state.refreshDataAfterCartPurchase = payload;
  },
  setPOPCharges(state, charges) {
    state.popCreditCharges = charges;
  },
};

const getters = {
  // reusable data accessors
  reviewOptimizationOrdersForPageRun: (state) => (pageRunId) => {
    return state.reviewOptimizationOrdersByPageRun[pageRunId] || [];
  },
  isSchemaLoading: (state) => {
    return state.genSchemaLoading;
  },
  aiWriterNeedsToSave: (state) => {
    return state.aiWriterNeedsToSave;
  },
  siloReportProgress: (state) => {
    return state.siloReportProgress;
  },
  isNewSiloUpdates: (state) => {
    return state.isNewSiloUpdates;
  },
  isNewTrackingUpdates: (state) => {
    return state.isNewTrackingUpdates;
  },
  selectedSiloKeywords: (state) => {
    return state.selectedSiloKeywords;
  },
  insightKeywordsConfig: (state) => {
    return state.insightKeywordsConfig;
  },
  asyncTasks: (state) => {
    return state.asyncTasks;
  },
  isDemoUser: () => {
    const { sub } = decodeToken(state.jwt) || {};
    return DEMO_USER_EMAILS.includes(sub);
  },
  // Signup redesign: an account that never had a plan (planState 'none'). It reads the example
  // project; creating projects, pages, runs and tools opens the plan-required modal.
  // Working inside an owner's account (sub-account mode), the OWNER's plan applies, as on the
  // backend guards: a no-plan login working for an agency must not see the example-project bar
  // (support, 2026-10-30).
  isNoPlanUser: (state) => {
    const relation = state.selectedSubAccountRelation;
    if (relation && relation.id) return Boolean(relation.ownerIsPlanRequired);
    return Boolean(state.user && state.user.isPlanRequired);
  },
  hasActivePlan: (state) => {
    const user = state.user || {};
    if (user.planState) {
      return ["admin", "exempt", "inherited", "active", "free_period"].includes(user.planState);
    }
    return Boolean(user.activeSubscriber);
  },
  isFirstVisit: (state) => {
    const user = state.user || {};
    return Boolean(user.id) && user.welcomeCompleted === false;
  },
  exampleProject: (state) => {
    return (state.user && state.user.exampleProject) || null;
  },
  aiWriterStep: (state) => {
    return state.aiWriterStep;
  },
  rockyReadback: (state) => {
    return state.rockyReadback;
  },
  getSiteData: (state) => {
    return state.sites;
  },
     getReportsData: (state) => {
    return state.reportsData;
  },
  isReportsLoading: (state) => {
    return state.reportsLoading;
  },
  getReportsError: (state) => {
    return state.reportsError;
  },
  getGoogleLocations: (state) => {
    return state.googleSearchLocations;
  },
  getAiWriterData: (state) => {
    return state.aiWriterData;
  },
  isProcess: (state) => {
    return state.process;
  },
  getSchemaProgresses: (state) => state.schemaProgresses,
  getModalData: (state) => state.modalData,
  getToken(state) {
    if (!state.jwt) {
      if (localStorage.getItem("token")) {
        return localStorage.getItem("token");
      }
      return sessionStorage.getItem("token") || false;
    }
    return state.jwt;
  },
  isAuthenticated(state, getters) {
    const token = getters.getToken;
    if (!token) {
      return false;
    }
    return isValidJwt(token);
  },
  isWatchdogOnlyUser(state) {
    if (state.user && watchdogOnlyServices.includes(state.user.accountType)) {
      return true;
    }
    return false;
  },
  /**
   * Account type in effect for the session: the selected sub-account owner's type when a
   * sub-account relation is active (e.g. mission control viewing a customer), else the user's.
   */
  effectiveAccountType(state) {
    if (!state.user) return null;
    const relation = state.selectedSubAccountRelation;
    return relation && typeof relation === "object"
      ? relation.ownerAccountType
      : state.user.accountType;
  },
  /** White glove family (White Glove plans + POP Authority): same services and screens. */
  isWhiteGloveFamilyUser(state, getters) {
    const accountType = getters.effectiveAccountType;
    return Boolean(accountType) && whiteGlovePlans.includes(getPureServicePkgName(accountType));
  },
  /** Strict POP Authority check; use only where POP Authority diverges from White Glove. */
  isPopAuthorityUser(state, getters) {
    const accountType = getters.effectiveAccountType;
    return Boolean(accountType) && popAuthorityPlans.includes(getPureServicePkgName(accountType));
  },
  /**
   * The program name in the screens both account types share (balance, cart, content order
   * form...): 'POP Authority' for a POP Authority account, 'White Glove' for everyone else.
   */
  whiteGloveFamilyProgramName(state, getters) {
    return getters.isPopAuthorityUser ? "POP Authority" : "White Glove";
  },
  requestSchemas(state) {
    return state.user?.requestedSchemas || [];
  },
  isSpecialAdminUser(state) {
    if (state.user && state.user.isAdmin && state.user.isSpecialAdmin) {
      return true;
    }
    return false;
  },
  isSpecialOwnerSelected(state) {
    if (state.user && state.selectedSubAccountRelation) {
      return state.selectedSubAccountRelation.isSpecialAdminOwner;
    }
    return false;
  },
  isAvailableToPurchaseAdditionalSeats(state) {
    const pureServicePkg = getPureServicePkgName(state.user.accountType);
    if (
      state.user &&
      agencyPlansWithAdditionalSeatsAvailable.includes(pureServicePkg)
    ) {
      return true;
    }
    return false;
  },
  additionalSeatPkgName(state) {
    if (state.user) {
      const pkg = correspondingSeatPlans[state.user.accountType]; // here you need to use full package name , not pure package name.
      return pkg || "";
    }
    return "";
  },
  getPages(state) {
    let pages = [];
    if (state.sites) {
      state.sites.forEach((site) => {
        pages = pages.concat(site.pages);
      });
    }
    return pages;
  },
  siteById: (state) => (id) => {
    if (!id) {
      return null;
    }
    return state.sites.find((site) => site.id === Number(id));
  },
  pageRunById: (state) => (id) => {
    return state.pageRuns.find((pageRun) => pageRun.id === id);
  },
  isCalculatingBulkNow(state) {
    for (let i = 0; i < state.bulkRuns.length; i++) {
      if (["PROGRESS", "PENDING"].includes(state.bulkRuns[i].status)) {
        return state.bulkRuns[i];
      }
      if (i < 3) {
        // just for performance update
        if (state.bulkRuns[i].status == "REVOKED") {
          for (const run of state.bulkRuns[i].allRuns) {
            if (run.isAIRun) {
              if (run.autoStatus != "SUCCESS" && run.autoStatus != "FAILURE") {
                return state.bulkRuns[i];
              }
            } else {
              if (run.status != "SUCCESS" && run.status != "FAILURE") {
                return state.bulkRuns[i];
              }
            }
          }
        }
      }
    }
    return false;
  },
  isUnlimitedUser(state) {
    if (!state.user) {
      return false;
    }
    if (state.user.isVendor) {
      return 0;
    } // because this is vendor, he can not make pagerun
    const accountType = getPureServicePkgName(state.user.accountType);
    if (UNLIMITED_SUBSCRIPTION_SERVICES.indexOf(accountType) > -1) {
      return 100;
    }
    if (accountType === "end_user") {
      return 100;
    }
    return false;
  },
  availablePOPKeys(state) {
    let rlt = [];
    if (!state.user) {
      return rlt;
    }
    let ownerAcc = state.user;
    if (state.selectedDevAccountRelation) {
      ownerAcc = state.selectedDevAccountRelation.ownerUser;
    }

    if (ownerAcc.addOnApiKey) {
      rlt.push({
        keyLabel: "Add-on API key",
        keyType: "ADD_ON_KEY",
        key: ownerAcc.addOnApiKey,
        dateCreated: ownerAcc.addOnApiKeyCreatedAt,
      });
    }
    if (ownerAcc.isDevApiKeyAllowed) {
      rlt.push({
        keyLabel: "Dev API key",
        keyType: "DEV_PACKAGE_KEY",
        key: ownerAcc.devApiKey,
        dateCreated: ownerAcc.devApiKeyCreatedAt,
      });
    }
    if (ownerAcc.isPartnershipApiKeyAllowed) {
      rlt.push({
        keyLabel: "Partership API Key",
        keyType: "PARTNER_KEY",
        key: ownerAcc.partnerApiKey,
        dateCreated: ownerAcc.partnerApiKeyCreatedAt,
      });
    }
    return rlt;
  },
  // hasSufficientFunds(state, getters) {
  //   if (state.user.isVendor) {
  //     return false;
  //   } // because this is vendor, he can not make pagerun
  //   if (state.selectedSubAccountRelation != "") return true;

  //   if (getters.isUnlimitedUser) {
  //     return true;
  //   }

  //   if (!state.user) {
  //     return false
  //   }
  //   let allCredits = state.user.monthlyCredits + state.user.remainedUniversalCredits
  //   return allCredits > 0;

  //   // if (accountType === "gold" || accountType === "trial") {
  //   //   return allCredits > 0;
  //   // }

  // },
  availableReportCreditsOfUserOrTeam(state, getters) {
    if (state.selectedSubAccountRelation) {
      return 999;
    }
    if (getters.isUnlimitedUser) {
      return 999;
    }

    return state.user.monthlyCredits + state.user.remainedUniversalCredits;
  },
  availableCreditsOfUserOrTeam(state) {
    let user = state.selectedSubAccountRelation || state.user;
    return user.monthlyCredits + user.remainedUniversalCredits;
  },
  isAgencyUnlimitedUser() {
    if (state.selectedSubAccountRelation) {
      return true;
    }
    const accountType = getPureServicePkgName(state.user.accountType);
    return AGENCY_PLANS.indexOf(accountType) > -1;
  },
  hasPOPApiKey(state) {
    if (!state.user) {
      return false;
    }
    return (
      state.user.addOnApiKey ||
      state.user.isDevApiKeyAllowed ||
      state.user.isPartnershipApiKeyAllowed
    );
  },
  hasApiDashboardAccess(state, getters) {
    if (getters.hasPOPApiKey) {
      return true;
    }
    if (state.selectedDevAccountRelation) {
      return true;
    }
    return false;
  },
  isPartnershipApiKeyAllowed(state) {
    return state.user.isPartnershipApiKeyAllowed;
  },
  isAdmin(state) {
    if (state.user && state.user.isAdmin) {
      return true;
    }
    return false;
  },
  hasAccessToRocky(state) {
    return !!(state.user && state.user.hasAccessToRocky);
  },
  isTrialUser(state) {
    if (state.user) {
      return state.user.isTrial;
    }
    return false;
  },
  getFilteredPages(state) {
    let pages = [];
    let criteria =
      state.menuSearchCriteria && state.menuSearchCriteria.toLowerCase();
    state.sites.map((site) =>
      site.pages.map((page) => {
        pages.push({ siteId: site.id, siteName: site.name, ...page });
      })
    );
    return pages
      .filter((x) => {
        if (state.menuSearchType === "pages") {
          return x.name && x.name.toLowerCase().indexOf(criteria) !== -1;
        }
      })
      .sort(sortByName);
  },
  getFilteredPagesByKeyword(state) {
    let pages = [];
    let criteria =
      state.menuSearchCriteria && state.menuSearchCriteria.toLowerCase();
    state.sites.map((site) =>
      site.pages.map((page) => {
        pages.push({ siteId: site.id, siteName: site.name, ...page });
      })
    );
    return pages
      .filter((x) => {
        if (state.menuSearchType === "keywords") {
          return x.keyword && x.keyword.toLowerCase().indexOf(criteria) !== -1;
        }
      })
      .sort(sortByKeyword);
  },
  isLoginOrRegister: (state) => (routeName) => {
    return [
      "Signup",
      "SignupVerify",
      "SignupSocialConfirm",
      "SignupAccountExists",
      "SignupPlans",
      "LoginCode",
      "ResetPassword",
      "Login",
      "PopDemo",
      "Home",
      "ForgotPassword",
      "RegisterCheckEmail",
      "UpdatePassword",
    ].includes(routeName);
  },
  clientSideAsyncTaskById: (state) => (id) => {
    return state.clientSideAsyncTasks.find((task) => task.id === id);
  },
  getPOPCharges(state) {
    return state.popCreditCharges;
  },
  isWhiteGloveAdminMode: (state) => state.isWhiteGloveAdminMode,
};

const store = new Vuex.Store({
  state,
  actions,
  mutations,
  getters,
  modules: { signup },
});

export default store;
